import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { useLocation, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBold, faCode, faListUl, faUnderline, faVideoCamera } from '@fortawesome/free-solid-svg-icons'
import defaultAvatar from '../Images/avatar1.png'
import { db, storage } from '../firebase'

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday']
let assignmentPromptCounter = 0
const createPromptItem = (html = '') => ({
  id: `prompt-${Date.now()}-${assignmentPromptCounter++}`,
  html
})

const emptyAssignmentForm = {
  title: '',
  assignmentWeekId: '',
  dueDate: '',
  resourceLink: '',
  instructions: '',
  assignmentType: 'questions_text',
  audience: 'all',
  selectedStudentIds: [],
  promptItems: [createPromptItem('')]
}

const MAX_ASSIGNMENT_ATTACHMENT_SIZE = 20 * 1024 * 1024

const assignmentTypeOptions = [
  {
    value: 'questions_text',
    label: 'Questions to answer non code',
    responseMode: 'text',
    supportsPromptItems: true
  },
  {
    value: 'questions_code',
    label: 'Question to answer code submission',
    responseMode: 'code',
    supportsPromptItems: true
  },
  {
    value: 'code_link',
    label: 'Code submission - GitHub link',
    responseMode: 'github',
    supportsPromptItems: false
  },
  {
    value: 'code_paste',
    label: 'Code submission - paste code',
    responseMode: 'code',
    supportsPromptItems: false
  },
  {
    value: 'project_demo',
    label: 'Project link or demo',
    responseMode: 'project',
    supportsPromptItems: false
  }
]

const normalizeText = (value) => String(value || '').trim()

const sanitizeStorageFileName = (value = 'file') => (
  String(value || 'file').replace(/[^a-zA-Z0-9._-]+/g, '_')
)

const formatFileSize = (value) => {
  const size = Number(value) || 0
  if (size <= 0) return ''
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${Math.round(size / 1024)} KB`
  return `${size} B`
}

const normalizeEmail = (value) => normalizeText(value).toLowerCase()

const normalizeCourseKey = (value) => normalizeText(value)
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const getAssignmentTypeConfig = (value) => (
  assignmentTypeOptions.find((option) => option.value === value) ||
  assignmentTypeOptions.find((option) => option.value === 'code_link')
)

const getAssignmentTypeLabel = (value, fallback = 'Task') => {
  if (value === 'code') return 'Code submission - GitHub link'
  if (value === 'questions') return 'Questions to answer'
  if (value === 'project') return 'Project link or demo'
  if (value === 'general') return 'General task / reflection'
  return getAssignmentTypeConfig(value)?.label || fallback
}

const normalizePromptItemsForEditor = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return [createPromptItem('')]
  return items.map((item) => (
    typeof item === 'string'
      ? createPromptItem(item)
      : {
          id: item?.id || createPromptItem('').id,
          html: item?.html || ''
        }
  ))
}

const sanitizeRichTextHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre', 'br', 'p', 'div', 'ul', 'ol', 'li', 'blockquote', 'span'])

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase()
        if (!allowedTags.has(tag)) {
          const fragment = doc.createDocumentFragment()
          while (child.firstChild) fragment.appendChild(child.firstChild)
          child.replaceWith(fragment)
          return
        }
        Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
        walk(child)
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
      }
    })
  }

  walk(doc.body)
  return doc.body.innerHTML
}

const getPlainTextFromHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim()
}

const escapeHtml = (input) => String(input || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const getCourseName = (course, fallback = 'Course') => {
  if (!course || typeof course !== 'object') return fallback
  return course.mcourseName || course.courseName || course.name || course.title || fallback
}

const buildProgressDocId = (studentId, courseName) => (
  `${studentId}_${String(courseName || '').replace(/\//g, '__')}`
)

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value.seconds === 'number') return value.seconds * 1000
  return new Date(value).getTime() || 0
}

const normalizeList = (value, limit = Infinity) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        return normalizeText(item.text || item.title || item.description || item.body)
      })
      .filter(Boolean)
      .slice(0, limit)
  }
  if (typeof value === 'string') {
    return value
      .split(/\r?\n|•|;/)
      .map((item) => item.trim())
      .filter(Boolean)
      .slice(0, limit)
  }
  return []
}

const normalizeDiagnoses = (items = [], limit = 5) => (
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      topic: normalizeText(item?.topic),
      subtopic: normalizeText(item?.subtopic),
      misconception: normalizeText(item?.misconception),
      evidence: normalizeText(item?.evidence),
      bestIntervention: normalizeText(item?.bestIntervention || item?.best_intervention)
    }))
    .filter((item) => item.topic || item.subtopic || item.misconception)
    .slice(0, limit)
)

const formatFireflyDate = (value) => {
  const timestamp = Number(value) || 0
  if (!timestamp) return 'Session date not captured'

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(timestamp))
}

const formatFireflyMiniDate = (value) => {
  const timestamp = Number(value) || 0
  if (!timestamp) return 'Session'

  return new Intl.DateTimeFormat('en-KE', {
    month: 'short',
    day: 'numeric'
  }).format(new Date(timestamp))
}

const formatFireflyDuration = (value) => {
  const totalSeconds = Math.max(0, Number(value) || 0)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes <= 0) return `${seconds}s`
  if (seconds === 0) return `${minutes} min`
  return `${minutes} min ${seconds}s`
}

const normalizeDailyPlan = (days = []) => {
  if (!Array.isArray(days)) return []

  return days.map((day, index) => {
    if (typeof day === 'string') {
      return {
        day: `Day ${index + 1}`,
        label: weekdays[index] || '',
        focus: day
      }
    }

    return {
      day: day.day || `Day ${index + 1}`,
      label: day.label || day.weekday || day.date || weekdays[index] || '',
      focus: day.focus || day.topic || day.activities || day.description || 'Daily focus will be shared.',
      teachingAid: day.teachingAid || '',
      reference: day.reference || ''
    }
  })
}

const normalizeScheme = (value) => {
  if (!value) return []

  if (Array.isArray(value)) {
    return value.map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `week-${index + 1}-${item}`,
          week: String(index + 1),
          topic: item,
          objectives: 'Objectives will be shared.',
          activities: 'Learning activities will be shared.',
          teachingAid: 'Teaching aids will be shared.',
          reference: 'References will be shared.',
          dailyPlan: []
        }
      }

      return {
        id: item.id || `week-${index + 1}-${item.topic || `Week ${index + 1}`}`,
        week: item.week || String(index + 1),
        topic: item.topic || `Week ${index + 1}`,
        objectives: item.objectives || item.objective || 'Objectives will be shared.',
        activities: item.activities || item.learningActivities || 'Learning activities will be shared.',
        teachingAid: item.teachingAid || item.aid || 'Teaching aids will be shared.',
        reference: item.reference || 'References will be shared.',
        dailyPlan: normalizeDailyPlan(item.dailyPlan || item.days || item.weekDays || item.dailyBreakdown)
      }
    })
  }

  if (typeof value === 'string') {
    return value
      .split(/\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, index) => ({
        id: `week-${index + 1}-${item}`,
        week: String(index + 1),
        topic: item,
        objectives: 'Objectives will be shared.',
        activities: 'Learning activities will be shared.',
        teachingAid: 'Teaching aids will be shared.',
        reference: 'References will be shared.',
        dailyPlan: []
      }))
  }

  return []
}

const getWeekCompletionId = (week = {}, weekIndex = 0) => (
  week.id || week.topic || `week-${weekIndex + 1}`
)

const getAssignmentWeekLabel = (week = {}, weekIndex = 0) => {
  const weekNumber = normalizeText(week.week) || String(weekIndex + 1)
  const topic = normalizeText(week.topic)
  return topic ? `Week ${weekNumber} - ${topic}` : `Week ${weekNumber}`
}

const getWeekProgressUnits = (week = {}, weekIndex = 0) => {
  const weekId = getWeekCompletionId(week, weekIndex)
  const dailyPlan = Array.isArray(week.dailyPlan) ? week.dailyPlan : []

  if (!dailyPlan.length) {
    return [{
      key: `${weekId}::week`,
      day: 'Week',
      label: week.topic || `Week ${week.week || weekIndex + 1}`,
      focus: week.objectives || week.activities || 'Weekly work',
      teachingAid: week.teachingAid || '',
      reference: week.reference || ''
    }]
  }

  return dailyPlan.map((day, dayIndex) => ({
    ...day,
    key: `${weekId}::${normalizeText(day.id || day.day || `day-${dayIndex + 1}`) || `day-${dayIndex + 1}`}`
  }))
}

const expandCompletedDayItems = (progress = {}, scheme = []) => {
  const daySet = new Set(Array.isArray(progress.completedDayItems) ? progress.completedDayItems : [])
  const completedWeekIds = Array.isArray(progress.completedItems) ? progress.completedItems : []

  scheme.forEach((week, weekIndex) => {
    const weekId = getWeekCompletionId(week, weekIndex)
    if (!completedWeekIds.includes(weekId)) return
    getWeekProgressUnits(week, weekIndex).forEach((unit) => daySet.add(unit.key))
  })

  return daySet
}

const getCompletedWeekIdsFromDaySet = (daySet, scheme = []) => (
  scheme.reduce((accumulator, week, weekIndex) => {
    const weekId = getWeekCompletionId(week, weekIndex)
    const units = getWeekProgressUnits(week, weekIndex)
    if (units.length && units.every((unit) => daySet.has(unit.key))) {
      accumulator.push(weekId)
    }
    return accumulator
  }, [])
)

const getProgressPercent = (progress = {}, scheme = []) => {
  const units = scheme.flatMap((week, weekIndex) => getWeekProgressUnits(week, weekIndex))
  if (!units.length) return 0
  const daySet = expandCompletedDayItems(progress, scheme)
  return Math.round((Array.from(daySet).filter((key) => units.some((unit) => unit.key === key)).length / units.length) * 100)
}

const normalizeAssignedCourses = (value) => {
  if (!Array.isArray(value)) {
    return typeof value === 'string' && value.trim()
      ? [{ id: value.trim(), name: value.trim() }]
      : []
  }

  return value
    .map((course, index) => {
      if (typeof course === 'string') {
        return { id: course, name: course }
      }

      return {
        id: course.id || course.courseId || course.value || `course-${index + 1}`,
        name: course.name || course.mcourseName || course.courseName || course.title || ''
      }
    })
    .filter((course) => course.name)
}

const courseMatchesClass = (courseName, className) => {
  const courseKey = normalizeCourseKey(courseName)
  const classKey = normalizeCourseKey(className)

  if (!courseKey || !classKey) return false
  if (courseKey.includes(classKey) || classKey.includes(courseKey)) return true

  const pathwayAliases = {
    'product design': ['product design', 'ui ux', 'ux', 'design'],
    'software development': ['software', 'web development', 'mobile app', 'full stack', 'javascript', 'python'],
    'machine learning': ['machine learning', 'applied machine', 'ml'],
    'data science': ['data science', 'data'],
    blockchain: ['blockchain'],
    robotics: ['robotics', 'electronics', 'embedded', 'microcontroller']
  }

  return Object.entries(pathwayAliases).some(([pathway, terms]) => (
    classKey.includes(pathway) && terms.some((term) => courseKey.includes(term))
  ))
}

const chunkArray = (items, size) => {
  const chunks = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

const buildFireflyReadId = (instructorId, studentId, reportId) => (
  `${instructorId}_${studentId}_${reportId}`.replace(/[^a-zA-Z0-9_-]/g, '_')
)

const getTopCounts = (items = [], limit = 6) => {
  const counts = items.reduce((acc, item) => {
    const label = normalizeText(item)
    if (!label) return acc
    acc[label] = (acc[label] || 0) + 1
    return acc
  }, {})

  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((first, second) => second.value - first.value || first.label.localeCompare(second.label))
    .slice(0, limit)
}

const getSignalScore = (value) => {
  const signal = normalizeCourseKey(value)
  if (!signal) return 50
  if (signal.includes('high') || signal.includes('confident') || signal.includes('strong')) return 88
  if (signal.includes('improv') || signal.includes('rising') || signal.includes('better')) return 74
  if (signal.includes('steady') || signal.includes('stable')) return 58
  if (signal.includes('low') || signal.includes('drop') || signal.includes('uncertain')) return 34
  return 50
}

const getProfileFitScore = (value) => {
  const numericValue = Number(value)
  if (!Number.isNaN(numericValue) && numericValue > 0) {
    return Math.max(0, Math.min(100, numericValue))
  }

  const signal = normalizeCourseKey(value)
  if (!signal) return 50
  if (signal.includes('high') || signal.includes('strong') || signal.includes('best')) return 88
  if (signal.includes('medium') || signal.includes('moderate') || signal.includes('helpful')) return 64
  if (signal.includes('low') || signal.includes('limited') || signal.includes('weak')) return 36
  return 50
}

const getProfileBullets = (items = [], limit = 4) => (
  (Array.isArray(items) ? items : [])
    .map((item) => {
      if (typeof item === 'string') return { label: item, evidence: '', action: '' }
      return {
        label: normalizeText(item?.label || item?.title || item?.theme),
        evidence: normalizeText(item?.evidence || item?.reason || item?.description),
        action: normalizeText(item?.action || item?.nextStep || item?.guidance || item?.firstMove)
      }
    })
    .filter((item) => item.label || item.evidence || item.action)
    .slice(0, limit)
)

const normalizeGeneratedFireflyProfile = (profileDoc, fallbackCount = 0) => {
  if (!profileDoc?.profile) return null

  const profileData = profileDoc.profile || {}
  const profileMeta = profileDoc.profileMeta || {}
  const analyticsData = profileDoc.analytics || {}

  return {
    headline: normalizeText(profileData.summary?.headline) || 'Firefly learning profile',
    learnerSnapshot: normalizeText(profileData.summary?.learnerSnapshot || profileData.summary?.momentumNarrative),
    futureOfLearningLens: normalizeText(profileData.summary?.futureOfLearningLens),
    momentumNarrative: normalizeText(profileData.summary?.momentumNarrative),
    readinessBand: normalizeText(profileData.profileSignals?.readinessBand) || 'Building profile',
    evidenceStrength: normalizeText(profileMeta.evidenceStrength) || 'low',
    evidenceSummary: normalizeText(profileMeta.summary),
    sourceCount: Number(profileMeta.sourceCount) || Number(analyticsData.sessionCount) || fallbackCount,
    lastAnalyzedLabel: formatFireflyDate(
      profileDoc.lastAnalyzedAtMs
      || profileDoc.updatedAtMs
      || profileDoc.generatedAtMs
      || profileMeta.latestSourceAtMs
    ),
    learningModes: (Array.isArray(profileData.profileSignals?.learningModeFit) ? profileData.profileSignals.learningModeFit : [])
      .map((item) => ({
        mode: normalizeText(item?.mode),
        fit: normalizeText(item?.fit),
        evidence: normalizeText(item?.evidence),
        score: getProfileFitScore(item?.fit)
      }))
      .filter((item) => item.mode || item.fit || item.evidence),
    changeSinceLastUpdate: {
      summary: normalizeText(profileData.changeSinceLastUpdate?.summary),
      newStrengths: normalizeList(profileData.changeSinceLastUpdate?.newStrengths, 4),
      newRisks: normalizeList(profileData.changeSinceLastUpdate?.newRisks, 4),
      improvedSignals: normalizeList(profileData.changeSinceLastUpdate?.improvedSignals, 4),
      declinedSignals: normalizeList(profileData.changeSinceLastUpdate?.declinedSignals, 4)
    },
    sessionIntelligence: {
      preferredTeachingModes: normalizeList(profileData.sessionIntelligence?.preferredTeachingModes, 4),
      explanationFormatsThatWork: normalizeList(profileData.sessionIntelligence?.explanationFormatsThatWork, 4),
      frictionTriggers: normalizeList(profileData.sessionIntelligence?.frictionTriggers, 4),
      engagementPattern: normalizeText(profileData.sessionIntelligence?.engagementPattern),
      pacePattern: normalizeText(profileData.sessionIntelligence?.pacePattern),
      confidencePattern: normalizeText(profileData.sessionIntelligence?.confidencePattern)
    },
    conceptMasteryMap: {
      strongestThemes: getProfileBullets(profileData.conceptMasteryMap?.strongestThemes, 4),
      fragileThemes: getProfileBullets(profileData.conceptMasteryMap?.fragileThemes, 4),
      misconceptionThreads: getProfileBullets(profileData.conceptMasteryMap?.misconceptionThreads, 4)
    },
    diagnosticLens: {
      misconceptionRecurrence: getProfileBullets(profileData.diagnosticLens?.misconceptionRecurrence, 4)
    },
    studentActionPlan: {
      keepDoing: normalizeList(profileData.studentActionPlan?.keepDoing, 4),
      focusNextSessions: normalizeList(profileData.studentActionPlan?.focusNextSessions, 4),
      beforeNextSession: normalizeList(profileData.studentActionPlan?.beforeNextSession, 4),
      nextChallenge: normalizeText(profileData.studentActionPlan?.nextChallenge),
      reflectionPrompt: normalizeText(profileData.studentActionPlan?.reflectionPrompt)
    },
    studentFacing: {
      encouragement: normalizeText(profileData.studentFacing?.encouragement),
      whatIsGoingWell: normalizeList(profileData.studentFacing?.whatIsGoingWell, 4),
      focusNext: normalizeList(profileData.studentFacing?.focusNext, 4),
      whyThisMatters: normalizeText(profileData.studentFacing?.whyThisMatters),
      reflectionPrompt: normalizeText(profileData.studentFacing?.reflectionPrompt)
    },
    fireflyRecommendation: {
      shouldInviteLiveSession: Boolean(profileData.fireflyRecommendation?.shouldInviteLiveSession),
      urgency: normalizeText(profileData.fireflyRecommendation?.urgency) || 'low',
      studentSummary: normalizeText(profileData.fireflyRecommendation?.studentSummary),
      outreachReason: normalizeText(profileData.fireflyRecommendation?.outreachReason || profileData.fireflyRecommendation?.teacherReason),
      focusConcepts: getProfileBullets(profileData.fireflyRecommendation?.recommendedFocusConcepts, 4),
      sessionGoals: normalizeList(profileData.fireflyRecommendation?.sessionGoals, 4),
      inviteWithin: normalizeText(profileData.fireflyRecommendation?.inviteWithin)
    },
    tutoringPlaybook: {
      openingMoves: normalizeList(profileData.tutoringPlaybook?.openingMoves, 4),
      inSessionMoves: normalizeList(profileData.tutoringPlaybook?.inSessionMoves, 4),
      checksForUnderstanding: normalizeList(profileData.tutoringPlaybook?.checksForUnderstanding, 4),
      boardAndVisualStrategies: normalizeList(profileData.tutoringPlaybook?.boardAndVisualStrategies, 4)
    }
  }
}

const FireflyLineChart = ({ reports = [] }) => {
  const recentReports = [...reports]
    .sort((first, second) => (first.startedAtMs || 0) - (second.startedAtMs || 0))
    .slice(-8)
  const values = recentReports.length
    ? recentReports.map((report) => getSignalScore(report.confidenceTrend))
    : [0]
  const chartLeft = 36
  const chartRight = 296
  const chartTop = 26
  const chartBottom = 122
  const chartHeight = chartBottom - chartTop
  const yTicks = [0, 25, 50, 75, 100]
  const points = values.map((value, index) => {
    const x = values.length === 1 ? ((chartLeft + chartRight) / 2) : chartLeft + (index * ((chartRight - chartLeft) / Math.max(values.length - 1, 1)))
    const y = chartBottom - ((value / 100) * chartHeight)
    return { x, y, value, label: formatFireflyMiniDate(recentReports[index]?.endedAtMs || recentReports[index]?.startedAtMs) }
  })
  const polylinePoints = points.length ? points.map((point) => `${point.x},${point.y}`).join(' ') : `${chartLeft},${chartBottom}`

  return (
    <div className='instructorFireflyChart instructorFireflyLineChart'>
      <svg viewBox='0 0 320 170' role='img' aria-label='Confidence trend chart'>
        {yTicks.map((tick) => {
          const y = chartBottom - ((tick / 100) * chartHeight)
          return (
            <g key={`tick-${tick}`}>
              <line className='instructorFireflyLineGrid' x1={chartLeft} y1={y} x2={chartRight} y2={y} />
              <text x={chartLeft - 8} y={y + 4} textAnchor='end'>{tick}</text>
            </g>
          )
        })}
        <line className='instructorFireflyLineAxis' x1={chartLeft} y1={chartBottom} x2={chartRight} y2={chartBottom} />
        <line className='instructorFireflyLineAxis' x1={chartLeft} y1={chartTop} x2={chartLeft} y2={chartBottom} />
        <polyline points={polylinePoints} />
        {points.map((point, index) => (
          <g key={`${point.value}-${index}`}>
            <circle cx={point.x} cy={point.y} r='4' />
            <text x={point.x} y='148' textAnchor='middle'>{point.label}</text>
          </g>
        ))}
        <text x='166' y='165' textAnchor='middle'>Sessions</text>
        <text x='14' y='76' textAnchor='middle' transform='rotate(-90 14 76)'>Confidence</text>
      </svg>
    </div>
  )
}

const FireflyBarChart = ({ items = [], emptyLabel = 'No data yet' }) => {
  const maxValue = Math.max(...items.map((item) => item.value), 1)

  return (
    <div className='instructorFireflyBars'>
      {(items.length ? items : [{ label: emptyLabel, value: 0 }]).map((item) => (
        <div key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          <i style={{ width: `${Math.max(6, (item.value / maxValue) * 100)}%` }} />
        </div>
      ))}
    </div>
  )
}

const FireflyBalanceChart = ({ strengths = [], struggles = [] }) => {
  const total = Math.max(strengths.length + struggles.length, 1)
  const strengthShare = Math.round((strengths.length / total) * 100)

  return (
    <div className='instructorFireflyBalance'>
      <div style={{ '--strength-share': `${strengthShare}%` }} />
      <section>
        <span><b /> Strengths {strengths.length}</span>
        <span><b /> Gaps {struggles.length}</span>
      </section>
    </div>
  )
}

function MyCoursesComp() {
  const auth = getAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [loadMessage, setLoadMessage] = useState('')
  const [instructor, setInstructor] = useState(null)
  const [instructorId, setInstructorId] = useState('')
  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [classStudents, setClassStudents] = useState([])
  const [studentProfiles, setStudentProfiles] = useState({})
  const [fireflyProfiles, setFireflyProfiles] = useState({})
  const [fireflyGeneratedProfiles, setFireflyGeneratedProfiles] = useState({})
  const [fireflyReports, setFireflyReports] = useState({})
  const [fireflyReadMap, setFireflyReadMap] = useState({})
  const [selectedFireflyStudentId, setSelectedFireflyStudentId] = useState('')
  const [selectedFireflyReportId, setSelectedFireflyReportId] = useState('')
  const [isFireflyProfileOpen, setIsFireflyProfileOpen] = useState(false)
  const [fireflyReportMode, setFireflyReportMode] = useState('general')
  const [assignments, setAssignments] = useState([])
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([])
  const [assignmentForm, setAssignmentForm] = useState(emptyAssignmentForm)
  const [savingAssignment, setSavingAssignment] = useState(false)
  const [assignmentNotice, setAssignmentNotice] = useState('')
  const assignmentEditorRef = useRef(null)
  const promptEditorRefs = useRef({})
  const assignmentAttachmentInputRef = useRef(null)
  const [assignmentAttachmentFile, setAssignmentAttachmentFile] = useState(null)
  const [courseProgressMap, setCourseProgressMap] = useState({})
  const [selectedProgressStudentIds, setSelectedProgressStudentIds] = useState([])
  const [progressClassFilter, setProgressClassFilter] = useState('all')
  const [progressNotice, setProgressNotice] = useState('')
  const [savingProgressKey, setSavingProgressKey] = useState('')
  const [activeCourseId, setActiveCourseId] = useState('')
  const [activeTab, setActiveTab] = useState('overview')

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false)
        setLoadMessage('Please sign in to view your teaching courses.')
        return
      }

      setLoading(true)
      setLoadMessage('')

      try {
        const employeeQuery = query(collection(db, 'Employees'), where('docId', '==', user.uid))
        const employeeSnapshot = await getDocs(employeeQuery)
        const employeeDoc = employeeSnapshot.docs[0]

        if (!employeeDoc) {
          setInstructor(null)
          setCourses([])
          setLoadMessage('Your instructor profile was not found in Employees.')
          return
        }

        const employeeData = { id: employeeDoc.id, ...employeeDoc.data() }
        const assignedCourses = normalizeAssignedCourses(
          employeeData.teachableCourses || employeeData.coursesCanTeach || employeeData.assignedCourses || employeeData.course
        )

        setInstructor(employeeData)
        setInstructorId(user.uid)

        if (assignedCourses.length === 0) {
          setCourses([])
          setLoadMessage('No courses have been assigned to your instructor profile yet.')
          return
        }

        const [courseSnapshot, classesSnapshot, assignmentsSnapshot, fireflyReadsSnapshot] = await Promise.all([
          getDocs(collection(db, 'courses')),
          getDocs(query(collection(db, 'allclasses'), where('instructor', '==', user.uid))),
          getDocs(query(collection(db, 'courseAssignments'), where('instructorId', '==', user.uid))),
          getDocs(query(collection(db, 'fireflyReportReads'), where('instructorId', '==', user.uid)))
        ])

        const classList = classesSnapshot.docs
          .map((classDoc) => ({ id: classDoc.id, ...classDoc.data() }))
          .sort((first, second) => normalizeTimestamp(second.timeCreated) - normalizeTimestamp(first.timeCreated))

        const studentSnapshots = await Promise.all(
          classList
            .map((classItem) => classItem.roomID || classItem.id)
            .filter(Boolean)
            .map((classId) => getDocs(query(collection(db, 'StudentsInClass'), where('classID', '==', classId))))
        )

        const studentMemberships = studentSnapshots.flatMap((snapshot) => (
          snapshot.docs.map((studentDoc) => ({ id: studentDoc.id, ...studentDoc.data() }))
        ))

        const studentIds = Array.from(new Set(
          studentMemberships
            .map((studentItem) => studentItem.studentID || studentItem.userId)
            .filter(Boolean)
        ))

        const studentProfileSnapshots = await Promise.all(
          chunkArray(studentIds, 10).map((studentChunk) => (
            getDocs(query(collection(db, 'users'), where('userId', 'in', studentChunk)))
          ))
        )

        const fireflySnapshots = await Promise.all(
          studentIds.map(async (studentId) => {
            try {
              const [profileSnap, sessionsSnap, generatedProfileSnap] = await Promise.all([
                getDoc(doc(db, 'plm', studentId)),
                getDocs(collection(db, 'plm', studentId, 'sessions')),
                getDoc(doc(db, 'studentFireflyLearningProfiles', studentId))
              ])

              const profileData = profileSnap.exists() ? profileSnap.data() : {}
              const sessions = sessionsSnap.docs
                .map((sessionDoc) => {
                  const data = sessionDoc.data() || {}
                  return {
                    id: sessionDoc.id,
                    status: normalizeText(data.status),
                    summary: normalizeText(data.summary),
                    sessionFocus: normalizeText(data.sessionFocus),
                    startedAtMs: Number(data.startedAtMs) || 0,
                    endedAtMs: Number(data.endedAtMs) || 0,
                    durationSeconds: Number(data.durationSeconds) || 0,
                    topicsCovered: normalizeList(data.topicsCovered, 8),
                    strengths: normalizeList(data.strengths, 5),
                    struggles: normalizeList(data.struggles, 5),
                    misconceptions: normalizeList(data.misconceptions, 5),
                    diagnoses: normalizeDiagnoses(data.diagnoses, 5),
                    supportStrategies: normalizeList(data.supportStrategies, 5),
                    recommendedNextSteps: normalizeList(data.recommendedNextSteps, 5),
                    preferredTeachingModes: normalizeList(data.preferredTeachingModes, 5),
                    teachingMethodsUsed: normalizeList(data.teachingMethodsUsed, 5),
                    confidenceTrend: normalizeText(data.confidenceTrend),
                    learningPace: normalizeText(data.learningPace),
                    userTurns: Number(data.userTurns) || 0,
                    assistantTurns: Number(data.assistantTurns) || 0
                  }
                })
                .filter((report) => report.status !== 'started')
                .sort((first, second) => (second.endedAtMs || second.startedAtMs) - (first.endedAtMs || first.startedAtMs))

              return {
                studentId,
                profile: profileSnap.exists()
                  ? {
                      summary: normalizeText(profileData.lastSessionSummary),
                      strengths: normalizeList(profileData.strengths, 5),
                      struggles: normalizeList(profileData.struggles, 5),
                      misconceptions: normalizeList(profileData.misconceptions, 5),
                      diagnoses: normalizeDiagnoses(profileData.diagnoses, 5),
                      preferredTeachingModes: normalizeList(profileData.preferredTeachingModes || profileData.preferredModesSeen, 5),
                      supportStrategies: normalizeList(profileData.supportStrategies, 5),
                      recommendedNextSteps: normalizeList(profileData.recommendedNextSteps, 5),
                      topicsSeen: normalizeList(profileData.topicsSeen || profileData.lastTopicsCovered, 8),
                      learningPace: normalizeText(profileData.learningPace),
                      confidenceTrend: normalizeText(profileData.confidenceTrend),
                      currentLessonObjective: normalizeText(profileData.currentLessonObjective),
                      lastSessionEndedAt: normalizeTimestamp(profileData.lastSessionEndedAt),
                      sessionCount: Number(profileData.sessionCount) || sessions.length
                    }
                  : null,
                generatedProfile: normalizeGeneratedFireflyProfile(
                  generatedProfileSnap.exists() ? { id: generatedProfileSnap.id, ...generatedProfileSnap.data() } : null,
                  sessions.length
                ),
                reports: sessions
              }
            } catch (error) {
              console.error('Error loading Firefly reports for student:', studentId, error)
              return { studentId, profile: null, generatedProfile: null, reports: [] }
            }
          })
        )

        const profileMap = {}
        studentProfileSnapshots.forEach((snapshot) => {
          snapshot.docs.forEach((studentDoc) => {
            const studentData = studentDoc.data()
            const studentId = studentData.userId || studentDoc.id
            profileMap[studentId] = { id: studentDoc.id, ...studentData }
          })
        })

        const assignmentList = assignmentsSnapshot.docs
          .map((assignmentDoc) => ({ id: assignmentDoc.id, ...assignmentDoc.data() }))
          .sort((first, second) => normalizeTimestamp(second.createdAt) - normalizeTimestamp(first.createdAt))

        const assignmentIds = assignmentList.map((assignment) => assignment.id).filter(Boolean)
        const submissionSnapshots = await Promise.all(
          chunkArray(assignmentIds, 10).map((assignmentChunk) => (
            getDocs(query(collection(db, 'assignmentSubmissions'), where('assignmentId', 'in', assignmentChunk)))
          ))
        )

        const allCourses = courseSnapshot.docs.map((courseDoc) => ({
          id: courseDoc.id,
          ...courseDoc.data()
        }))

        const resolvedCourses = assignedCourses.map((assignedCourse) => {
          const assignedId = normalizeText(assignedCourse.id)
          const assignedNameKey = normalizeCourseKey(assignedCourse.name)
          const match = allCourses.find((course) => (
            course.id === assignedId ||
            course.courseId === assignedId ||
            normalizeCourseKey(getCourseName(course)) === assignedNameKey
          ))

          if (match) return match

          return {
            id: assignedCourse.id,
            mcourseName: assignedCourse.name,
            missingDetails: true
          }
        })

        setCourses(resolvedCourses)
        setClasses(classList)
        setClassStudents(studentMemberships)
        setStudentProfiles(profileMap)
        setFireflyProfiles(fireflySnapshots.reduce((profiles, item) => {
          profiles[item.studentId] = item.profile
          return profiles
        }, {}))
        setFireflyGeneratedProfiles(fireflySnapshots.reduce((profiles, item) => {
          profiles[item.studentId] = item.generatedProfile
          return profiles
        }, {}))
        setFireflyReports(fireflySnapshots.reduce((reports, item) => {
          reports[item.studentId] = item.reports
          return reports
        }, {}))
        setFireflyReadMap(fireflyReadsSnapshot.docs.reduce((reads, readDoc) => {
          reads[readDoc.id] = true
          return reads
        }, {}))
        setAssignments(assignmentList)
        setAssignmentSubmissions(submissionSnapshots.flatMap((snapshot) => (
          snapshot.docs.map((submissionDoc) => ({ id: submissionDoc.id, ...submissionDoc.data() }))
        )))
        setActiveCourseId((current) => current || resolvedCourses[0]?.id || '')
      } catch (error) {
        console.error('Error loading instructor courses:', error)
        setLoadMessage('Could not load your courses right now. Please try again shortly.')
      } finally {
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth])

  const activeCourse = useMemo(() => {
    return courses.find((course) => course.id === activeCourseId) || courses[0] || null
  }, [activeCourseId, courses])

  const overview = useMemo(() => normalizeList(
    activeCourse?.overview || activeCourse?.courseOverview || activeCourse?.curriculumOverview || activeCourse?.aboutProgram
  ), [activeCourse])

  const deliverables = useMemo(() => normalizeList(
    activeCourse?.deliverables || activeCourse?.courseDeliverables || activeCourse?.outcomes || activeCourse?.learningOutcomes
  ), [activeCourse])

  const scheme = useMemo(() => normalizeScheme(
    activeCourse?.schemeOfWork || activeCourse?.scheme || activeCourse?.courseScheme || activeCourse?.schemeTemplate
  ), [activeCourse])

  const schemeWeekOptions = useMemo(() => (
    scheme.map((week, weekIndex) => ({
      id: getWeekCompletionId(week, weekIndex),
      week: normalizeText(week.week) || String(weekIndex + 1),
      topic: normalizeText(week.topic),
      label: getAssignmentWeekLabel(week, weekIndex)
    }))
  ), [scheme])

  const activeCourseClasses = useMemo(() => {
    if (!activeCourse) return []
    const activeCourseName = getCourseName(activeCourse)
    return classes.filter((classItem) => courseMatchesClass(activeCourseName, classItem.classDesc))
  }, [activeCourse, classes])

  const activeCourseStudents = useMemo(() => {
    const connectedClassIds = new Set(
      activeCourseClasses.map((classItem) => classItem.roomID || classItem.id).filter(Boolean)
    )

    const studentMap = new Map()

    classStudents
      .filter((studentItem) => connectedClassIds.has(studentItem.classID))
      .forEach((studentItem) => {
        const studentId = studentItem.studentID || studentItem.userId || studentItem.id
        if (!studentId || studentMap.has(studentId)) return
        const profile = studentProfiles[studentId] || {}
        studentMap.set(studentId, {
          ...studentItem,
          name: studentItem.name || profile.bio || profile.name,
          studentEmail: studentItem.studentEmail || profile.userEmail,
          course: studentItem.course || profile.course,
          profilePic: studentItem.profilePic || profile.profilePic
        })
      })

    return Array.from(studentMap.values())
  }, [activeCourseClasses, classStudents, studentProfiles])

  const activeCourseStudentCount = activeCourseStudents.length

  useEffect(() => {
    const courseName = getCourseName(activeCourse, '')
    const activeStudentIds = new Set(
      activeCourseStudents
        .map((studentItem) => studentItem.studentID || studentItem.userId || studentItem.id)
        .filter(Boolean)
    )

    if (!courseName || !activeStudentIds.size) {
      setCourseProgressMap({})
      return
    }

    let isMounted = true

    getDocs(query(collection(db, 'courseProgress'), where('courseName', '==', courseName)))
      .then((snapshot) => {
        if (!isMounted) return

        const nextProgressMap = {}
        snapshot.docs.forEach((progressDoc) => {
          const progressData = progressDoc.data() || {}
          const studentId = progressData.userId
          if (!studentId || !activeStudentIds.has(studentId)) return
          nextProgressMap[studentId] = {
            id: progressDoc.id,
            ...progressData
          }
        })

        setCourseProgressMap(nextProgressMap)
      })
      .catch((error) => {
        console.error('Error loading course progress:', error)
      })

    return () => {
      isMounted = false
    }
  }, [activeCourse, activeCourseStudents])

  useEffect(() => {
    const visibleStudentIds = (
      progressClassFilter === 'all'
        ? activeCourseStudents
        : activeCourseStudents.filter((studentItem) => (
            (studentItem.classID || studentItem.roomID || studentItem.classId) === progressClassFilter
          ))
    )
      .map((studentItem) => studentItem.studentID || studentItem.userId || studentItem.id)
      .filter(Boolean)

    setSelectedProgressStudentIds((current) => current.filter((studentId) => visibleStudentIds.includes(studentId)))
  }, [activeCourseStudents, progressClassFilter])

  const filteredProgressStudents = useMemo(() => {
    if (progressClassFilter === 'all') return activeCourseStudents
    return activeCourseStudents.filter((studentItem) => (
      (studentItem.classID || studentItem.roomID || studentItem.classId) === progressClassFilter
    ))
  }, [activeCourseStudents, progressClassFilter])

  const studentProgressRows = useMemo(() => (
    activeCourseStudents.map((studentItem) => {
      const studentId = studentItem.studentID || studentItem.userId || studentItem.id
      const progress = courseProgressMap[studentId] || { completedItems: [], completedDayItems: [] }
      const completedWeekIds = getCompletedWeekIdsFromDaySet(expandCompletedDayItems(progress, scheme), scheme)
      return {
        ...studentItem,
        studentId,
        progress,
        completedWeekIds,
        progressPercent: getProgressPercent(progress, scheme)
      }
    }).filter((item) => item.studentId)
  ), [activeCourseStudents, courseProgressMap, scheme])

  const schemeCompletionStats = useMemo(() => (
    scheme.map((week, weekIndex) => {
      const weekId = getWeekCompletionId(week, weekIndex)
      const completedCount = studentProgressRows.filter((row) => row.completedWeekIds.includes(weekId)).length
      return {
        weekId,
        completedCount
      }
    })
  ), [scheme, studentProgressRows])

  const buildStudentProfilePreview = (studentItem = {}) => ({
    studentId: studentItem.studentID || studentItem.studentId || studentItem.userId || '',
    name: studentItem.name || studentItem.studentName || studentItem.bio || studentItem.studentEmail || studentItem.userEmail || 'Student',
    email: studentItem.studentEmail || studentItem.userEmail || studentItem.email || '',
    avatar: studentItem.profilePic || studentItem.avatar || studentItem.photoURL || '',
    course: studentItem.course || getCourseName(activeCourse)
  })

  const openStudentProfile = (studentLike = {}) => {
    const candidateId = studentLike.studentID || studentLike.studentId || studentLike.userId || studentLike.submittedById
    const candidateEmail = normalizeEmail(studentLike.studentEmail || studentLike.userEmail || studentLike.email)

    const resolvedStudent = activeCourseStudents.find((studentItem) => {
      const studentId = studentItem.studentID || studentItem.studentId || studentItem.userId || studentItem.id
      const studentEmail = normalizeEmail(studentItem.studentEmail || studentItem.userEmail || studentItem.email)
      return (candidateId && studentId === candidateId) || (candidateEmail && studentEmail === candidateEmail)
    }) || classStudents.find((studentItem) => {
      const studentId = studentItem.studentID || studentItem.studentId || studentItem.userId || studentItem.id
      const studentEmail = normalizeEmail(studentItem.studentEmail || studentItem.userEmail || studentItem.email)
      return (candidateId && studentId === candidateId) || (candidateEmail && studentEmail === candidateEmail)
    }) || (candidateId && studentProfiles[candidateId]) || null

    const preview = buildStudentProfilePreview({ ...resolvedStudent, ...studentLike })
    if (!preview.studentId) return

    navigate(`/student-profile/${preview.studentId}`, {
      state: {
        searchStudentPreview: preview
      }
    })
  }

  const activeCourseFireflyRows = useMemo(() => (
    activeCourseStudents.map((studentItem) => {
      const studentId = studentItem.studentID || studentItem.userId || studentItem.id
      const reports = fireflyReports[studentId] || []
      const unreadReports = reports.filter((report) => !fireflyReadMap[buildFireflyReadId(instructorId, studentId, report.id)])
      return {
        studentId,
        student: studentItem,
        profile: fireflyProfiles[studentId] || null,
        generatedProfile: fireflyGeneratedProfiles[studentId] || null,
        reports,
        unreadReports
      }
    }).filter((row) => row.studentId)
  ), [activeCourseStudents, fireflyGeneratedProfiles, fireflyProfiles, fireflyReadMap, fireflyReports, instructorId])

  const activeCourseFireflyReportCount = useMemo(() => (
    activeCourseFireflyRows.reduce((total, row) => total + row.reports.length, 0)
  ), [activeCourseFireflyRows])

  const activeCourseUnreadFireflyReportCount = useMemo(() => (
    activeCourseFireflyRows.reduce((total, row) => total + row.unreadReports.length, 0)
  ), [activeCourseFireflyRows])

  const selectedFireflyBundle = useMemo(() => (
    activeCourseFireflyRows.find((row) => row.studentId === selectedFireflyStudentId) ||
    activeCourseFireflyRows[0] ||
    null
  ), [activeCourseFireflyRows, selectedFireflyStudentId])

  const selectedFireflyReport = useMemo(() => {
    if (!selectedFireflyBundle) return null
    return selectedFireflyBundle.reports.find((report) => report.id === selectedFireflyReportId) ||
      selectedFireflyBundle.reports[0] ||
      null
  }, [selectedFireflyBundle, selectedFireflyReportId])

  const selectedGeneratedFireflyProfile = selectedFireflyBundle?.generatedProfile || null

  const selectedFireflyAnalytics = useMemo(() => {
    if (!selectedFireflyBundle) {
      return {
        topTopics: [],
        modeCounts: [],
        totalSeconds: 0,
        averageDuration: 0,
        totalDiagnoses: 0,
        totalTopics: 0
      }
    }

    const reports = selectedFireflyBundle.reports || []
    const topicItems = reports.flatMap((report) => [
      ...(report.topicsCovered || []),
      ...(report.diagnoses || []).flatMap((item) => [item.topic, item.subtopic])
    ])
    const modeItems = reports.flatMap((report) => [
      ...(report.teachingMethodsUsed || []),
      ...(report.preferredTeachingModes || [])
    ])
    const totalSeconds = reports.reduce((sum, report) => sum + (Number(report.durationSeconds) || 0), 0)

    return {
      topTopics: getTopCounts(topicItems, 6),
      modeCounts: getTopCounts(modeItems, 5),
      totalSeconds,
      averageDuration: reports.length ? Math.round(totalSeconds / reports.length) : 0,
      totalDiagnoses: reports.reduce((sum, report) => sum + (report.diagnoses?.length || 0), 0),
      totalTopics: new Set(topicItems.map((item) => normalizeText(item)).filter(Boolean)).size
    }
  }, [selectedFireflyBundle])

  const selectedFireflyModeItems = useMemo(() => {
    if (selectedGeneratedFireflyProfile?.learningModes?.length) {
      return selectedGeneratedFireflyProfile.learningModes.map((item) => ({
        label: item.mode || item.fit || 'Mode fit',
        value: item.score || getProfileFitScore(item.fit)
      }))
    }

    return selectedFireflyAnalytics.modeCounts
  }, [selectedFireflyAnalytics.modeCounts, selectedGeneratedFireflyProfile])

  useEffect(() => {
    const state = location.state || {}
    if (!state.openTab && !state.openFireflyStudentId && !state.openFireflyReportId) return

    if (state.openTab === 'firefly' || state.openFireflyStudentId || state.openFireflyReportId) {
      setActiveTab('firefly')
      setIsFireflyProfileOpen(Boolean(state.openFireflyStudentId || state.openFireflyReportId))
      setFireflyReportMode(state.openFireflyReportId ? 'individual' : 'general')
      if (state.openFireflyStudentId) setSelectedFireflyStudentId(state.openFireflyStudentId)
      if (state.openFireflyReportId) setSelectedFireflyReportId(state.openFireflyReportId)
    }
  }, [location.state])

  useEffect(() => {
    if (!instructorId || !selectedFireflyBundle?.studentId || !selectedFireflyReport?.id) return
    if (!isFireflyProfileOpen || fireflyReportMode !== 'individual') return

    const readId = buildFireflyReadId(instructorId, selectedFireflyBundle.studentId, selectedFireflyReport.id)
    if (fireflyReadMap[readId]) return

    setFireflyReadMap((current) => ({ ...current, [readId]: true }))
    setDoc(doc(db, 'fireflyReportReads', readId), {
      instructorId,
      studentId: selectedFireflyBundle.studentId,
      reportId: selectedFireflyReport.id,
      readAt: serverTimestamp()
    }, { merge: true }).catch(() => {
      setFireflyReadMap((current) => {
        const next = { ...current }
        delete next[readId]
        return next
      })
    })
  }, [fireflyReadMap, fireflyReportMode, instructorId, isFireflyProfileOpen, selectedFireflyBundle, selectedFireflyReport])

  const activeCourseAssignments = useMemo(() => {
    if (!activeCourse) return []

    const activeCourseKey = normalizeCourseKey(getCourseName(activeCourse))

    return assignments.filter((assignment) => (
      assignment.courseId === activeCourse.id ||
      assignment.courseKey === activeCourseKey ||
      normalizeCourseKey(assignment.courseName) === activeCourseKey
    ))
  }, [activeCourse, assignments])

  const assignmentSubmissionStats = useMemo(() => {
    const stats = {}

    assignmentSubmissions.forEach((submission) => {
      if (!submission.assignmentId) return

      if (!stats[submission.assignmentId]) {
        stats[submission.assignmentId] = {
          total: 0,
          reviewed: 0,
          needsRevision: 0,
          submissions: []
        }
      }

      const submissionStatus = normalizeCourseKey(submission.status || submission.reviewStatus || 'submitted')
      stats[submission.assignmentId].total += 1
      stats[submission.assignmentId].submissions.push(submission)

      if (submissionStatus.includes('review')) {
        stats[submission.assignmentId].reviewed += 1
      }

      if (submissionStatus.includes('revision') || submissionStatus.includes('redo')) {
        stats[submission.assignmentId].needsRevision += 1
      }
    })

    return stats
  }, [assignmentSubmissions])

  const currentAssignmentTypeConfig = useMemo(
    () => getAssignmentTypeConfig(assignmentForm.assignmentType),
    [assignmentForm.assignmentType]
  )

  const selectedAssignmentWeek = useMemo(() => (
    schemeWeekOptions.find((option) => option.id === assignmentForm.assignmentWeekId) || null
  ), [assignmentForm.assignmentWeekId, schemeWeekOptions])

  useEffect(() => {
    if (!assignmentForm.assignmentWeekId) return
    if (schemeWeekOptions.some((option) => option.id === assignmentForm.assignmentWeekId)) return

    setAssignmentForm((current) => (
      current.assignmentWeekId
        ? { ...current, assignmentWeekId: '' }
        : current
    ))
  }, [assignmentForm.assignmentWeekId, schemeWeekOptions])

  const joinClass = (roomId) => {
    if (!roomId) return
    navigate('/class-session', { state: { myData: roomId } })
  }

  const openSchemeEditor = () => {
    if (!activeCourse?.id) return
    navigate(`/my-courses/${activeCourse.id}/edit-scheme`)
  }

  const toggleProgressStudent = (studentId) => {
    setSelectedProgressStudentIds((current) => (
      current.includes(studentId)
        ? current.filter((item) => item !== studentId)
        : [...current, studentId]
    ))
  }

  const selectVisibleProgressStudents = () => {
    const visibleStudentIds = filteredProgressStudents
      .map((studentItem) => studentItem.studentID || studentItem.userId || studentItem.id)
      .filter(Boolean)

    setSelectedProgressStudentIds(visibleStudentIds)
  }

  const clearProgressSelection = () => {
    setSelectedProgressStudentIds([])
  }

  const saveDayProgressForStudents = async (week, weekIndex, unit, complete = true) => {
    if (!activeCourse || !instructorId) return
    if (!selectedProgressStudentIds.length) {
      setProgressNotice('Select at least one student before marking progress.')
      return
    }

    const courseName = getCourseName(activeCourse)
    const actionKey = `${getWeekCompletionId(week, weekIndex)}-${unit.key}-${complete ? 'complete' : 'undo'}`
    setSavingProgressKey(actionKey)
    setProgressNotice('')

    try {
      const updates = await Promise.all(selectedProgressStudentIds.map(async (studentId) => {
        const existingProgress = courseProgressMap[studentId] || {
          completedItems: [],
          completedDayItems: []
        }
        const nextDaySet = expandCompletedDayItems(existingProgress, scheme)

        if (complete) {
          nextDaySet.add(unit.key)
        } else {
          nextDaySet.delete(unit.key)
        }

        const nextCompletedItems = getCompletedWeekIdsFromDaySet(nextDaySet, scheme)
        const progressDocId = existingProgress.id || buildProgressDocId(studentId, courseName)
        const payload = {
          userId: studentId,
          courseName,
          courseId: activeCourse.id || '',
          completedItems: nextCompletedItems,
          completedDayItems: Array.from(nextDaySet),
          updatedAt: serverTimestamp(),
          updatedAtMs: Date.now(),
          updatedByInstructorId: instructorId,
          updatedByInstructorName: instructor?.employeeName || 'Instructor'
        }

        await setDoc(doc(db, 'courseProgress', progressDocId), payload, { merge: true })

        return {
          studentId,
          progress: {
            id: progressDocId,
            ...existingProgress,
            ...payload,
            updatedAt: Date.now()
          }
        }
      }))

      setCourseProgressMap((current) => {
        const next = { ...current }
        updates.forEach(({ studentId, progress }) => {
          next[studentId] = progress
        })
        return next
      })

      setProgressNotice(`${complete ? 'Marked' : 'Cleared'} ${unit.day} for ${selectedProgressStudentIds.length} student${selectedProgressStudentIds.length === 1 ? '' : 's'}.`)
    } catch (error) {
      console.error('Error updating day progress:', error)
      setProgressNotice('Could not update progress right now. Please try again.')
    } finally {
      setSavingProgressKey('')
    }
  }

  const updateAssignmentForm = (field, value) => {
    setAssignmentForm((current) => ({ ...current, [field]: value }))
  }

  const updateAssignmentInstructions = () => {
    const rawHtml = assignmentEditorRef.current?.innerHTML || ''
    const cleanHtml = sanitizeRichTextHtml(rawHtml)
    setAssignmentForm((current) => ({ ...current, instructions: cleanHtml }))
  }

  const focusAssignmentEditor = () => {
    assignmentEditorRef.current?.focus()
  }

  const applyInstructionStyle = (command) => {
    focusAssignmentEditor()
    document.execCommand(command, false)
    updateAssignmentInstructions()
  }

  const insertInstructionList = () => {
    focusAssignmentEditor()
    document.execCommand('insertUnorderedList', false)
    updateAssignmentInstructions()
  }

  const insertInstructionCode = () => {
    focusAssignmentEditor()
    const selection = window.getSelection()
    const selectedText = selection ? selection.toString() : ''

    if (selectedText) {
      document.execCommand('insertHTML', false, `<code>${escapeHtml(selectedText)}</code>`)
    } else {
      document.execCommand('insertHTML', false, '<code>code</code>')
    }
    updateAssignmentInstructions()
  }

  const updateAssignmentType = (value) => {
    const nextConfig = getAssignmentTypeConfig(value)
    setAssignmentForm((current) => ({
      ...current,
      assignmentType: value,
      promptItems: nextConfig?.supportsPromptItems
        ? normalizePromptItemsForEditor(current.promptItems)
        : current.promptItems
    }))
  }

  const focusPromptEditor = (promptId) => {
    promptEditorRefs.current[promptId]?.focus()
  }

  const updateAssignmentPrompt = (promptId, value) => {
    setAssignmentForm((current) => ({
      ...current,
      promptItems: normalizePromptItemsForEditor(current.promptItems).map((item) => (
        item.id === promptId ? { ...item, html: value } : item
      ))
    }))
  }

  const updateAssignmentPromptHtml = (promptId) => {
    const rawHtml = promptEditorRefs.current[promptId]?.innerHTML || ''
    updateAssignmentPrompt(promptId, rawHtml)
  }

  const applyPromptStyle = (promptId, command) => {
    focusPromptEditor(promptId)
    document.execCommand(command, false)
    updateAssignmentPromptHtml(promptId)
  }

  const insertPromptList = (promptId) => {
    focusPromptEditor(promptId)
    document.execCommand('insertUnorderedList', false)
    updateAssignmentPromptHtml(promptId)
  }

  const insertPromptCode = (promptId) => {
    focusPromptEditor(promptId)
    const selection = window.getSelection()
    const selectedText = selection ? selection.toString() : ''

    if (selectedText) {
      document.execCommand('insertHTML', false, `<code>${escapeHtml(selectedText)}</code>`)
    } else {
      document.execCommand('insertHTML', false, '<code>code</code>')
    }
    updateAssignmentPromptHtml(promptId)
  }

  const addAssignmentPrompt = () => {
    const nextPrompt = createPromptItem('')
    setAssignmentForm((current) => ({
      ...current,
      promptItems: [...normalizePromptItemsForEditor(current.promptItems), nextPrompt]
    }))
    window.setTimeout(() => {
      promptEditorRefs.current[nextPrompt.id]?.focus()
    }, 0)
  }

  const removeAssignmentPrompt = (promptId) => {
    setAssignmentForm((current) => {
      const nextPromptItems = normalizePromptItemsForEditor(current.promptItems).filter((item) => item.id !== promptId)
      delete promptEditorRefs.current[promptId]
      return {
        ...current,
        promptItems: nextPromptItems.length ? nextPromptItems : [createPromptItem('')]
      }
    })
  }

  useEffect(() => {
    ;(Array.isArray(assignmentForm.promptItems) ? assignmentForm.promptItems : []).forEach((prompt) => {
      const node = promptEditorRefs.current[prompt.id]
      if (!node) return
      if (document.activeElement === node) return

      const nextHtml = prompt.html || ''
      if (node.innerHTML !== nextHtml) {
        node.innerHTML = nextHtml
      }
    })
  }, [assignmentForm.promptItems])

  const toggleAssignmentStudent = (studentId) => {
    setAssignmentForm((current) => {
      const selectedStudentIds = current.selectedStudentIds.includes(studentId)
        ? current.selectedStudentIds.filter((id) => id !== studentId)
        : [...current.selectedStudentIds, studentId]

      return { ...current, selectedStudentIds }
    })
  }

  const handleAssignmentAttachmentChange = (event) => {
    const nextFile = event.target.files?.[0] || null

    if (nextFile && nextFile.size > MAX_ASSIGNMENT_ATTACHMENT_SIZE) {
      setAssignmentAttachmentFile(null)
      setAssignmentNotice('Keep the uploaded assignment file under 20 MB.')
      event.target.value = ''
      return
    }

    setAssignmentAttachmentFile(nextFile)
    if (assignmentNotice === 'Keep the uploaded assignment file under 20 MB.') {
      setAssignmentNotice('')
    }
  }

  const clearAssignmentAttachment = () => {
    setAssignmentAttachmentFile(null)
    if (assignmentAttachmentInputRef.current) assignmentAttachmentInputRef.current.value = ''
  }

  const uploadAssignmentAttachment = async (file, instructorUid, courseKey) => {
    if (!file) return null
    if (!storage) {
      throw new Error('Storage is unavailable right now.')
    }

    const safeFileName = sanitizeStorageFileName(file.name || 'assignment-file')
    const storageRef = ref(
      storage,
      `assignment-resources/${instructorUid}/${courseKey || 'course'}/${Date.now()}_${safeFileName}`
    )
    const uploadTask = uploadBytesResumable(storageRef, file)

    await new Promise((resolve, reject) => {
      uploadTask.on('state_changed', undefined, reject, resolve)
    })

    const url = await getDownloadURL(uploadTask.snapshot.ref)

    return {
      name: file.name || safeFileName,
      url,
      path: uploadTask.snapshot.ref.fullPath,
      size: file.size || 0,
      contentType: file.type || '',
      uploadedAtMs: Date.now()
    }
  }

  const createAssignment = async (event) => {
    event.preventDefault()
    setAssignmentNotice('')

    if (!activeCourse || !instructorId) return

    const assignmentTypeConfig = getAssignmentTypeConfig(assignmentForm.assignmentType)
    const promptItems = normalizePromptItemsForEditor(assignmentForm.promptItems)
      .map((item) => sanitizeRichTextHtml(item.html || ''))
      .filter((item) => getPlainTextFromHtml(item))
    const promptItemsText = promptItems
      .map((item) => getPlainTextFromHtml(item))
      .filter(Boolean)
    const instructionsHtml = sanitizeRichTextHtml(assignmentForm.instructions)
    const instructionsText = getPlainTextFromHtml(instructionsHtml)
    const selectedWeekOption = schemeWeekOptions.find((option) => option.id === assignmentForm.assignmentWeekId) || null
    const selectedWeekRecord = selectedWeekOption
      ? scheme.find((week, weekIndex) => getWeekCompletionId(week, weekIndex) === selectedWeekOption.id) || null
      : null

    if (!assignmentForm.title.trim()) {
      setAssignmentNotice('Add an assignment title before publishing.')
      return
    }

    if (schemeWeekOptions.length > 0 && !selectedWeekOption) {
      setAssignmentNotice('Choose which week in the weekly teaching plan this assignment belongs to.')
      return
    }

    if (assignmentTypeConfig?.supportsPromptItems && promptItems.length === 0) {
      setAssignmentNotice('Add at least one question or prompt for this assignment type.')
      return
    }

    if (!assignmentTypeConfig?.supportsPromptItems && !instructionsText) {
      setAssignmentNotice('Add the task instructions before publishing.')
      return
    }

    if (assignmentForm.audience === 'selected' && assignmentForm.selectedStudentIds.length === 0) {
      setAssignmentNotice('Select at least one student or switch the assignment audience to all students.')
      return
    }

    setSavingAssignment(true)

    try {
      const now = new Date().toISOString()
      const courseName = getCourseName(activeCourse)
      const courseKey = normalizeCourseKey(courseName)
      const connectedClassIds = activeCourseClasses.map((classItem) => classItem.roomID || classItem.id).filter(Boolean)
      const allStudentIds = activeCourseStudents
        .map((studentItem) => studentItem.studentID || studentItem.userId || studentItem.id)
        .filter(Boolean)
      const studentIds = assignmentForm.audience === 'all'
        ? allStudentIds
        : assignmentForm.selectedStudentIds
      const assignmentAttachment = await uploadAssignmentAttachment(assignmentAttachmentFile, instructorId, courseKey)

      const assignmentData = {
        title: assignmentForm.title.trim(),
        instructions: instructionsText,
        instructionsHtml,
        instructionsText,
        assignmentType: assignmentForm.assignmentType,
        assignmentTypeLabel: getAssignmentTypeLabel(assignmentForm.assignmentType, 'Task'),
        assignmentResponseMode: assignmentTypeConfig?.responseMode || 'text',
        promptItems,
        promptItemsText,
        audience: assignmentForm.audience,
        assignmentWeekId: selectedWeekOption?.id || '',
        assignmentWeek: selectedWeekOption?.week || '',
        assignmentWeekTopic: selectedWeekOption?.topic || '',
        assignmentWeekLabel: selectedWeekOption?.label || '',
        assignmentWeekObjectives: selectedWeekRecord?.objectives || '',
        assignmentWeekActivities: selectedWeekRecord?.activities || '',
        assignmentWeekTeachingAid: selectedWeekRecord?.teachingAid || '',
        assignmentWeekReference: selectedWeekRecord?.reference || '',
        assignmentWeekDailyPlan: Array.isArray(selectedWeekRecord?.dailyPlan)
          ? selectedWeekRecord.dailyPlan.map((day) => ({
              day: day.day || '',
              label: day.label || '',
              focus: day.focus || '',
              teachingAid: day.teachingAid || '',
              reference: day.reference || ''
            }))
          : [],
        dueDate: assignmentForm.dueDate,
        resourceLink: assignmentForm.resourceLink.trim(),
        assignmentAttachment,
        courseId: activeCourse.id || '',
        courseName,
        courseKey,
        instructorId,
        instructorName: instructor?.employeeName || 'Instructor',
        classIds: connectedClassIds,
        studentIds,
        status: 'open',
        createdAt: now,
        updatedAt: now
      }

      const assignmentRef = await addDoc(collection(db, 'courseAssignments'), assignmentData)
      setAssignments((current) => [{ id: assignmentRef.id, ...assignmentData }, ...current])
      setAssignmentForm(emptyAssignmentForm)
      clearAssignmentAttachment()
      if (assignmentEditorRef.current) assignmentEditorRef.current.innerHTML = ''
      promptEditorRefs.current = {}
      setAssignmentNotice('Assignment published for this course.')
      navigate('/assignments', {
        state: {
          openAssignmentId: assignmentRef.id
        }
      })
    } catch (error) {
      console.error('Error creating assignment:', error)
      setAssignmentNotice('Could not publish the assignment. Please try again.')
    } finally {
      setSavingAssignment(false)
    }
  }

  if (loading) {
    return (
      <div className='instructorCoursesPage'>
        <div className='instructorCoursesState'>
          <div className='spinner-border text-dark' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <span>Loading your courses...</span>
        </div>
      </div>
    )
  }

  if (!activeCourse) {
    return (
      <div className='instructorCoursesPage'>
        <div className='instructorCoursesState'>
          <h5>No teaching courses yet</h5>
          <p>{loadMessage || 'Ask admin to assign courses to your instructor profile.'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='instructorCoursesPage'>
      <section className='instructorCoursesShell'>
        <div className='instructorCoursesTabBar'>
          <span className='instructorCoursesEyebrow'>Teaching list</span>
          <div className='instructorCoursesList'>
            {courses.map((course) => (
              <button
                key={course.id}
                type='button'
                className={activeCourse?.id === course.id ? 'instructorCourseMiniTab active' : 'instructorCourseMiniTab'}
                onClick={() => {
                  setActiveCourseId(course.id)
                  setActiveTab('overview')
                }}
              >
                {getCourseName(course)}
              </button>
            ))}
          </div>
        </div>

        <main className='instructorCoursesMain'>
          <section className='instructorCourseHeader'>
            <div>
              <span className='instructorCoursesEyebrow'>Course details</span>
              <h3>{getCourseName(activeCourse)}</h3>
              <p>{activeCourse.mcourseDescription || activeCourse.courseDescription || activeCourse.description || 'No course description has been added yet.'}</p>
            </div>
            <div className='instructorCourseHeaderStats'>
              <div>
                <span>Duration</span>
                <strong>{activeCourse.mcourseDuration || activeCourse.courseDuration || 'Not set'}</strong>
              </div>
              <div>
                <span>Students</span>
                <strong>{activeCourseStudentCount}</strong>
              </div>
            </div>
          </section>

          <div className='instructorCourseTabs'>
            <button type='button' className={activeTab === 'overview' ? 'active' : ''} onClick={() => setActiveTab('overview')}>
              Overview
            </button>
            <button type='button' className={activeTab === 'scheme' ? 'active' : ''} onClick={() => setActiveTab('scheme')}>
              Scheme of Work
            </button>
            <button type='button' className={activeTab === 'progress' ? 'active' : ''} onClick={() => setActiveTab('progress')}>
              Progress
            </button>
            <button type='button' className={activeTab === 'classes' ? 'active' : ''} onClick={() => setActiveTab('classes')}>
              Classes
            </button>
            <button type='button' className={activeTab === 'students' ? 'active' : ''} onClick={() => setActiveTab('students')}>
              Students
            </button>
            <button type='button' className={activeTab === 'firefly' ? 'active' : ''} onClick={() => setActiveTab('firefly')}>
              Firefly Reports
            </button>
            <button type='button' className={activeTab === 'assignments' ? 'active' : ''} onClick={() => setActiveTab('assignments')}>
              Assignments
            </button>
          </div>

          {activeTab === 'overview' && (
            <section className='instructorCourseGrid'>
              <article className='instructorCoursePanel'>
                <div className='instructorCoursePanelHeader'>
                  <span className='instructorCoursesEyebrow'>Overview</span>
                  <h5>Curriculum overview</h5>
                </div>
                {overview.length > 0 ? (
                  <ul className='instructorCourseList'>
                    {overview.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                ) : (
                  <p className='instructorCourseMuted'>No curriculum overview has been published for this course yet.</p>
                )}
              </article>

              <article className='instructorCoursePanel'>
                <div className='instructorCoursePanelHeader'>
                  <span className='instructorCoursesEyebrow'>Outcomes</span>
                  <h5>Course deliverables</h5>
                </div>
                {deliverables.length > 0 ? (
                  <ul className='instructorCourseList'>
                    {deliverables.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                ) : (
                  <p className='instructorCourseMuted'>No deliverables have been published for this course yet.</p>
                )}
              </article>
            </section>
          )}

          {activeTab === 'scheme' && (
            <section className='instructorCoursePanel instructorCoursePanelFull'>
              <div className='instructorCoursePanelHeader'>
                <div>
                  <span className='instructorCoursesEyebrow'>Scheme</span>
                  <h5>Weekly teaching plan</h5>
                </div>
                <div className='instructorCoursePanelActions'>
                  <span className='instructorCourseCount'>{scheme.length} week{scheme.length === 1 ? '' : 's'}</span>
                  <button type='button' className='instructorCourseActionButton' onClick={openSchemeEditor}>
                    Edit
                  </button>
                </div>
              </div>

              {scheme.length > 0 ? (
                <div className='instructorSchemeList'>
                  {scheme.map((week) => (
                    <article className='instructorSchemeWeek' key={week.id || `${week.week}-${week.topic}`}>
                      <div className='instructorSchemeWeekHeader'>
                        <span>Week {week.week}</span>
                        <div>
                          <h5>{week.topic}</h5>
                          <p>{week.objectives}</p>
                        </div>
                        <div className='instructorSchemeCompletionBadge'>
                          {schemeCompletionStats.find((item) => item.weekId === getWeekCompletionId(week))?.completedCount || 0}/{activeCourseStudentCount} completed
                        </div>
                      </div>

                      {week.dailyPlan.length > 0 ? (
                        <div className='instructorSchemeTableWrap'>
                          <table className='instructorSchemeTable'>
                            <thead>
                              <tr>
                                <th>Day</th>
                                <th>Class Day</th>
                                <th>Daily Activity</th>
                                <th>Teaching Aid</th>
                                <th>Reference</th>
                              </tr>
                            </thead>
                            <tbody>
                              {week.dailyPlan.map((day) => (
                                <tr key={`${week.id}-${day.day}-${day.label}`}>
                                  <td>{day.day}</td>
                                  <td>{day.label}</td>
                                  <td>{day.focus}</td>
                                  <td>{day.teachingAid || week.teachingAid}</td>
                                  <td>{day.reference || week.reference}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className='instructorCourseMuted'>No daily plan has been added for this week.</p>
                      )}

                      <div className='instructorSchemeMeta'>
                        <div>
                          <span>Activities</span>
                          <p>{week.activities}</p>
                        </div>
                        <div>
                          <span>Teaching Aid</span>
                          <p>{week.teachingAid}</p>
                        </div>
                        <div>
                          <span>Reference</span>
                          <p>{week.reference}</p>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p className='instructorCourseMuted'>No scheme of work has been published for this course yet.</p>
              )}
            </section>
          )}

          {activeTab === 'progress' && (
            <section className='instructorCoursePanel instructorCoursePanelFull'>
              <div className='instructorCoursePanelHeader'>
                <div>
                  <span className='instructorCoursesEyebrow'>Progress</span>
                  <h5>Track course completion</h5>
                </div>
                <div className='instructorCoursePanelActions'>
                  <span className='instructorCourseCount'>{selectedProgressStudentIds.length} selected</span>
                </div>
              </div>

              <div className='instructorProgressToolbar'>
                <div className='instructorProgressFilter'>
                  <label>Class group</label>
                  <select
                    className='form-select editInput instructorProgressSelect'
                    value={progressClassFilter}
                    onChange={(event) => setProgressClassFilter(event.target.value)}
                  >
                    <option value='all'>All connected classes</option>
                    {activeCourseClasses.map((classItem) => (
                      <option key={classItem.roomID || classItem.id} value={classItem.roomID || classItem.id}>
                        {classItem.classDesc || 'Class'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className='instructorProgressActions'>
                  <button type='button' className='instructorCourseActionButton' onClick={selectVisibleProgressStudents}>
                    Select Visible
                  </button>
                  <button type='button' className='instructorCourseActionButton' onClick={clearProgressSelection}>
                    Clear Selection
                  </button>
                </div>
              </div>

              {progressNotice ? (
                <div className='instructorProgressNotice'>
                  {progressNotice}
                </div>
              ) : null}

              <div className='instructorProgressStudentGrid'>
                {studentProgressRows
                  .filter((row) => progressClassFilter === 'all' || row.classID === progressClassFilter)
                  .map((row) => {
                    const isChecked = selectedProgressStudentIds.includes(row.studentId)
                    return (
                      <article key={row.studentId} className={`instructorProgressStudentCard ${isChecked ? 'selected' : ''}`}>
                        <label className='instructorProgressStudentCheck'>
                          <input
                            type='checkbox'
                            checked={isChecked}
                            onChange={() => toggleProgressStudent(row.studentId)}
                          />
                          <span />
                        </label>
                        <img src={row.profilePic || defaultAvatar} alt='' className='defaultAvatar' />
                        <div className='instructorProgressStudentBody'>
                          <strong>{row.name || row.studentEmail || 'Student'}</strong>
                          <p>{row.studentEmail || 'No email shared'}</p>
                          <div className='instructorProgressStudentMeta'>
                            <span>{row.progressPercent}% complete</span>
                            <span>{row.completedWeekIds.length}/{scheme.length} weeks</span>
                          </div>
                        </div>
                      </article>
                    )
                  })}
              </div>

              <div className='instructorProgressWeekList'>
                {scheme.map((week, weekIndex) => {
                  const units = getWeekProgressUnits(week, weekIndex)
                  const selectedRows = studentProgressRows.filter((row) => selectedProgressStudentIds.includes(row.studentId))
                  const weekCompletedCount = schemeCompletionStats.find((item) => item.weekId === getWeekCompletionId(week, weekIndex))?.completedCount || 0

                  return (
                    <article className='instructorProgressWeekCard' key={getWeekCompletionId(week, weekIndex)}>
                      <div className='instructorProgressWeekHeader'>
                        <div>
                          <span>Week {week.week}</span>
                          <h6>{week.topic}</h6>
                          <p>{week.objectives}</p>
                        </div>
                        <div className='instructorProgressWeekStats'>
                          <strong>{weekCompletedCount}/{activeCourseStudentCount}</strong>
                          <span>students completed this week</span>
                        </div>
                      </div>

                      <div className='instructorProgressTableWrap'>
                        <table className='instructorProgressTable'>
                          <thead>
                            <tr>
                              <th>Day</th>
                              <th>Class Day</th>
                              <th>Daily Activity</th>
                              <th>Selected Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {units.map((unit) => {
                              const selectedCompletedCount = selectedRows.filter((row) => {
                                const daySet = expandCompletedDayItems(row.progress, scheme)
                                return daySet.has(unit.key)
                              }).length
                              const actionKeyBase = `${getWeekCompletionId(week, weekIndex)}-${unit.key}`

                              return (
                                <tr key={unit.key}>
                                  <td>{unit.day}</td>
                                  <td>{unit.label}</td>
                                  <td>{unit.focus}</td>
                                  <td>
                                    <span className='instructorProgressDayStatus'>
                                      {selectedCompletedCount}/{selectedRows.length || 0} selected complete
                                    </span>
                                  </td>
                                  <td>
                                    <div className='instructorProgressDayActions'>
                                      <button
                                        type='button'
                                        className='instructorCourseActionButton'
                                        disabled={!selectedProgressStudentIds.length || savingProgressKey === `${actionKeyBase}-complete`}
                                        onClick={() => saveDayProgressForStudents(week, weekIndex, unit, true)}
                                      >
                                        {savingProgressKey === `${actionKeyBase}-complete` ? 'Saving...' : 'Mark Complete'}
                                      </button>
                                      <button
                                        type='button'
                                        className='instructorProgressUndoButton'
                                        disabled={!selectedProgressStudentIds.length || savingProgressKey === `${actionKeyBase}-undo`}
                                        onClick={() => saveDayProgressForStudents(week, weekIndex, unit, false)}
                                      >
                                        {savingProgressKey === `${actionKeyBase}-undo` ? 'Saving...' : 'Undo'}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </article>
                  )
                })}
              </div>
            </section>
          )}

          {activeTab === 'classes' && (
            <section className='instructorCoursePanel instructorCoursePanelFull'>
              <div className='instructorCoursePanelHeader'>
                <div>
                  <span className='instructorCoursesEyebrow'>Classes</span>
                  <h5>Your teaching schedule</h5>
                </div>
                <span className='instructorCourseCount'>{activeCourseClasses.length} connected</span>
              </div>

              {activeCourseClasses.length > 0 ? (
                <div className='instructorClassesGrid instructorCourseRoomsGrid'>
                  {activeCourseClasses.map((classItem) => (
                    <article className='teamsItem2 instructorCourseRoomCard' key={classItem.id}>
                      <div className='teamsItemProfilecont2'>
                        <div className='roomTopdesc'>
                          <div className='defaultAvatarCont'>
                            <img src={instructor?.profilePic || defaultAvatar} className='defaultAvatar' alt='' />
                          </div>
                          <div className='roomTopContent'>
                            <h6>{classItem.classDesc || 'Class'}</h6>
                            <p><FontAwesomeIcon icon={faVideoCamera} /> {instructor?.employeeName || 'Your classroom'}</p>
                          </div>
                        </div>

                        <div className='roomTimeClasses'>
                          <div>
                            <p className='roomTimeClassesH'>Cohort Date</p>
                            <p className='roomTimeClassesP'>{classItem.intakeDate || 'Not set'}</p>
                          </div>
                          <div>
                            <p className='roomTimeClassesH'>Start Time</p>
                            <p className='roomTimeClassesP'>{classItem.startTime || '--:--'}</p>
                          </div>
                          <div>
                            <p className='roomTimeClassesH2'>End Time</p>
                            <p className='roomTimeClassesP2'>{classItem.endTime || '--:--'}</p>
                          </div>
                        </div>

                        <div>
                          <button
                            type='button'
                            className='joinMyClass'
                            disabled={!classItem.roomID}
                            onClick={() => joinClass(classItem.roomID)}
                          >
                            Join Class
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p className='instructorCourseMuted'>No classes connected to {getCourseName(activeCourse)} have been assigned to your instructor account yet.</p>
              )}
            </section>
          )}

          {activeTab === 'students' && (
            <section className='instructorCoursePanel instructorCoursePanelFull'>
              <div className='instructorCoursePanelHeader'>
                <div>
                  <span className='instructorCoursesEyebrow'>Students</span>
                  <h5>Students in this class</h5>
                </div>
                <span className='instructorCourseCount'>{activeCourseStudents.length} student{activeCourseStudents.length === 1 ? '' : 's'}</span>
              </div>

              {activeCourseStudents.length > 0 ? (
                <div className='instructorCourseStudentsList'>
                  {activeCourseStudents.map((studentItem) => (
                    <button
                      type='button'
                      className='instructorCourseStudentCard'
                      key={studentItem.studentID || studentItem.id}
                      onClick={() => openStudentProfile(studentItem)}
                    >
                      <div className='instructorCourseStudentAvatar'>
                        {studentItem.profilePic ? (
                          <img src={studentItem.profilePic} alt='' />
                        ) : (
                          (studentItem.name || studentItem.studentEmail || 'S').charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h6>{studentItem.name || 'Student'}</h6>
                        <p>{studentItem.studentEmail || 'Email not added'}</p>
                      </div>
                      <div className='instructorCourseStudentMeta'>
                        <span>{studentItem.course || getCourseName(activeCourse)}</span>
                        <small>Cohort: {studentItem.cohortDate || 'Not set'}</small>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className='instructorCourseMuted'>No students connected to {getCourseName(activeCourse)} have been added to this class yet.</p>
              )}
            </section>
          )}

          {activeTab === 'firefly' && (
            <section className='instructorCoursePanel instructorCoursePanelFull'>
              <div className='instructorCoursePanelHeader'>
                <div>
                  <span className='instructorCoursesEyebrow'>Firefly reports</span>
                  <h5>{isFireflyProfileOpen ? 'Learner profile' : 'Student learning intelligence'}</h5>
                </div>
                <span className='instructorCourseCount'>{activeCourseFireflyReportCount} report{activeCourseFireflyReportCount === 1 ? '' : 's'}</span>
                {activeCourseUnreadFireflyReportCount > 0 && (
                  <span className='instructorCourseCount instructorFireflyUnreadCount'>{activeCourseUnreadFireflyReportCount} unread</span>
                )}
              </div>

              {activeCourseFireflyRows.length > 0 ? (
                isFireflyProfileOpen && selectedFireflyBundle ? (
                  <div className='instructorFireflyFullScreen'>
                    <div className='instructorFireflyFullTop'>
                      <button
                        type='button'
                        onClick={() => {
                          setIsFireflyProfileOpen(false)
                          setSelectedFireflyReportId('')
                        }}
                      >
                        Back to students
                      </button>
                      <div className='instructorFireflyFullIdentity'>
                        <span className='instructorCourseStudentAvatar instructorFireflyHeroAvatar'>
                          {selectedFireflyBundle.student.profilePic ? (
                            <img src={selectedFireflyBundle.student.profilePic} alt='' />
                          ) : (
                            (selectedFireflyBundle.student.name || selectedFireflyBundle.student.studentEmail || 'S').charAt(0).toUpperCase()
                          )}
                        </span>
                        <div>
                          <span className='instructorCoursesEyebrow'>Learner profile</span>
                          <h4>{selectedFireflyBundle.student.name || 'Student'}</h4>
                          <p>{selectedFireflyBundle.student.studentEmail || 'Email not added'}</p>
                        </div>
                      </div>
                      <div className='instructorFireflyModeSwitch'>
                        <button type='button' className={fireflyReportMode === 'general' ? 'active' : ''} onClick={() => setFireflyReportMode('general')}>
                          General Report
                        </button>
                        <button type='button' className={fireflyReportMode === 'individual' ? 'active' : ''} onClick={() => setFireflyReportMode('individual')}>
                          Individual Reports
                          {selectedFireflyBundle.unreadReports.length > 0 && (
                            <span className='instructorFireflyModeBadge'>{selectedFireflyBundle.unreadReports.length}</span>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className='instructorFireflyStudentHero instructorFireflyFullHero'>
                      <div>
                        <span className='instructorCoursesEyebrow'>Firefly summary</span>
                        <h5>{selectedGeneratedFireflyProfile?.headline || selectedFireflyBundle.profile?.currentLessonObjective || 'Adaptive learning profile'}</h5>
                        <p>{selectedGeneratedFireflyProfile?.learnerSnapshot || selectedFireflyBundle.profile?.summary || 'Firefly has not generated a learner profile summary for this student yet.'}</p>
                      </div>
                      <div className='instructorFireflyStats instructorFireflyMetricGrid'>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricSessions'>
                          <span>Reviewed sessions</span>
                          <strong>{selectedGeneratedFireflyProfile?.sourceCount || selectedFireflyBundle.reports.length}</strong>
                        </div>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricEvidence'>
                          <span>Evidence strength</span>
                          <strong>{selectedGeneratedFireflyProfile?.evidenceStrength || 'live'}</strong>
                        </div>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricReadiness'>
                          <span>Readiness band</span>
                          <strong>{selectedGeneratedFireflyProfile?.readinessBand || 'building profile'}</strong>
                        </div>
                        <div className='instructorFireflyMetricCard instructorFireflyMetricAnalysis'>
                          <span>Last analysis</span>
                          <strong>{selectedGeneratedFireflyProfile?.lastAnalyzedLabel || formatFireflyDate(selectedFireflyReport?.endedAtMs || selectedFireflyReport?.startedAtMs)}</strong>
                        </div>
                      </div>
                    </div>

                    {fireflyReportMode === 'general' ? (
                      <>
                        {selectedGeneratedFireflyProfile?.evidenceSummary && (
                          <div className='instructorFireflySummaryCard'>
                            <span className='instructorCoursesEyebrow'>Profile freshness</span>
                            <p>{selectedGeneratedFireflyProfile.evidenceSummary}</p>
                          </div>
                        )}

                        {selectedGeneratedFireflyProfile?.fireflyRecommendation?.shouldInviteLiveSession && (
                          <div className='instructorFireflySummaryCard instructorFireflySummaryCardAccent'>
                            <span className='instructorCoursesEyebrow'>Firefly support recommendation</span>
                            <h6>{selectedGeneratedFireflyProfile.fireflyRecommendation.studentSummary || 'Firefly is recommending a live support session based on the latest tutoring signals.'}</h6>
                            {selectedGeneratedFireflyProfile.fireflyRecommendation.focusConcepts.length > 0 && (
                              <div className='instructorFireflyChipGroup'>
                                {selectedGeneratedFireflyProfile.fireflyRecommendation.focusConcepts.map((item) => (
                                  <span key={`focus-${item.label}`} className='instructorFireflyChip instructorFireflyChipWarn'>{item.label}</span>
                                ))}
                              </div>
                            )}
                            {selectedGeneratedFireflyProfile.fireflyRecommendation.outreachReason && (
                              <p>{selectedGeneratedFireflyProfile.fireflyRecommendation.outreachReason}</p>
                            )}
                          </div>
                        )}

                        <div className='instructorFireflyInsightGrid'>
                          <article className='instructorFireflyChartPanel instructorFireflyChartPanelWide'>
                            <div className='instructorFireflyChartHead'>
                              <div>
                                <span className='instructorCoursesEyebrow'>Trend</span>
                                <h6>Learning rhythm</h6>
                              </div>
                              <strong>{selectedFireflyBundle.profile?.confidenceTrend || 'Learning'}</strong>
                            </div>
                            <FireflyLineChart reports={selectedFireflyBundle.reports} />
                          </article>

                          <article className='instructorFireflyChartPanel'>
                            <div className='instructorFireflyChartHead'>
                              <div>
                                <span className='instructorCoursesEyebrow'>Topics</span>
                                <h6>Most visited concepts</h6>
                              </div>
                            </div>
                            <FireflyBarChart items={selectedFireflyAnalytics.topTopics} emptyLabel='No topics yet' />
                          </article>

                          <article className='instructorFireflyChartPanel'>
                            <div className='instructorFireflyChartHead'>
                              <div>
                                <span className='instructorCoursesEyebrow'>Teaching</span>
                                <h6>{selectedGeneratedFireflyProfile?.learningModes?.length ? 'Learning mode fit' : 'Methods Firefly used'}</h6>
                              </div>
                            </div>
                            <FireflyBarChart items={selectedFireflyModeItems} emptyLabel='No methods yet' />
                          </article>

                          <article className='instructorFireflyChartPanel'>
                            <div className='instructorFireflyChartHead'>
                              <div>
                                <span className='instructorCoursesEyebrow'>Balance</span>
                                <h6>Strengths vs gaps</h6>
                              </div>
                              <strong>{selectedFireflyBundle.profile?.learningPace || 'Adaptive'}</strong>
                            </div>
                            <FireflyBalanceChart
                              strengths={selectedFireflyBundle.profile?.strengths || []}
                              struggles={selectedFireflyBundle.profile?.struggles || []}
                            />
                          </article>
                        </div>

                        {selectedGeneratedFireflyProfile ? (
                          <>
                            <section className='instructorFireflyGeneratedBlock'>
                              <div className='instructorFireflyChartHead instructorFireflyGeneratedHead'>
                                <div>
                                  <span className='instructorCoursesEyebrow'>Adaptive profile</span>
                                  <h6>What changed and how to teach better next</h6>
                                  <p>{selectedGeneratedFireflyProfile.futureOfLearningLens || selectedGeneratedFireflyProfile.momentumNarrative || 'This profile updates when new Firefly evidence changes the learning pattern.'}</p>
                                </div>
                              </div>

                              <div className='instructorFireflyGeneratedGrid'>
                                <div className='instructorFireflySoftCard'>
                                  <h6>What changed since the last update</h6>
                                  <p>{selectedGeneratedFireflyProfile.changeSinceLastUpdate.summary || 'As new sessions shift the evidence, this section will explain what improved, what appeared, and what needs closer support.'}</p>
                                  <div className='instructorFireflyChipGroup'>
                                    {selectedGeneratedFireflyProfile.changeSinceLastUpdate.newStrengths.map((item) => (
                                      <span key={`new-strength-${item}`} className='instructorFireflyChip instructorFireflyChipSuccess'>{item}</span>
                                    ))}
                                    {selectedGeneratedFireflyProfile.changeSinceLastUpdate.improvedSignals.map((item) => (
                                      <span key={`improved-${item}`} className='instructorFireflyChip instructorFireflyChipNeutral'>{item}</span>
                                    ))}
                                    {selectedGeneratedFireflyProfile.changeSinceLastUpdate.newRisks.map((item) => (
                                      <span key={`new-risk-${item}`} className='instructorFireflyChip instructorFireflyChipWarn'>{item}</span>
                                    ))}
                                    {selectedGeneratedFireflyProfile.changeSinceLastUpdate.declinedSignals.map((item) => (
                                      <span key={`declined-${item}`} className='instructorFireflyChip instructorFireflyChipWarn'>{item}</span>
                                    ))}
                                  </div>
                                </div>

                                <div className='instructorFireflySoftCard'>
                                  <h6>How Firefly helps this learner learn best</h6>
                                  <p>{selectedGeneratedFireflyProfile.sessionIntelligence.engagementPattern || selectedGeneratedFireflyProfile.sessionIntelligence.pacePattern || 'The tutoring profile is still learning the best pacing and explanation style for this learner.'}</p>
                                  <div className='instructorFireflyChipGroup'>
                                    {selectedGeneratedFireflyProfile.sessionIntelligence.preferredTeachingModes.map((item) => (
                                      <span key={`preferred-mode-${item}`} className='instructorFireflyChip'>{item}</span>
                                    ))}
                                    {selectedGeneratedFireflyProfile.sessionIntelligence.explanationFormatsThatWork.map((item) => (
                                      <span key={`format-${item}`} className='instructorFireflyChip instructorFireflyChipAlt'>{item}</span>
                                    ))}
                                  </div>
                                </div>

                                <div className='instructorFireflySoftCard'>
                                  <h6>What is going well</h6>
                                  <ul>
                                    {(selectedGeneratedFireflyProfile.studentFacing.whatIsGoingWell.length
                                      ? selectedGeneratedFireflyProfile.studentFacing.whatIsGoingWell
                                      : ['The strongest recurring wins will appear here once Firefly sees repeated positive evidence.']).map((item, index) => (
                                      <li key={`well-${index}`}>{item}</li>
                                    ))}
                                  </ul>
                                </div>

                                <div className='instructorFireflySoftCard'>
                                  <h6>Focus for the next sessions</h6>
                                  <ul>
                                    {(selectedGeneratedFireflyProfile.studentFacing.focusNext.length
                                      ? selectedGeneratedFireflyProfile.studentFacing.focusNext
                                      : ['The next support priorities will appear here once a stable tutoring pattern is detected.']).map((item, index) => (
                                      <li key={`focus-${index}`}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </section>

                            <div className='instructorFireflyTwoCol'>
                              <div className='instructorFireflySection'>
                                <h6>Strongest themes</h6>
                                <div className='instructorFireflyDiagnosisList'>
                                  {(selectedGeneratedFireflyProfile.conceptMasteryMap.strongestThemes.length
                                    ? selectedGeneratedFireflyProfile.conceptMasteryMap.strongestThemes
                                    : [{ label: 'Still building profile', evidence: 'Firefly needs more repeated evidence to lock in the strongest themes.' }]).map((item, index) => (
                                    <article key={`strong-theme-${index}`} className='instructorFireflyDiagnosisCard'>
                                      <div>
                                        <span>Theme</span>
                                        <p>{item.label}</p>
                                      </div>
                                      {item.evidence && (
                                        <div>
                                          <span>Evidence</span>
                                          <p>{item.evidence}</p>
                                        </div>
                                      )}
                                    </article>
                                  ))}
                                </div>
                              </div>

                              <div className='instructorFireflySection'>
                                <h6>Fragile themes</h6>
                                <div className='instructorFireflyDiagnosisList'>
                                  {(selectedGeneratedFireflyProfile.conceptMasteryMap.fragileThemes.length
                                    ? selectedGeneratedFireflyProfile.conceptMasteryMap.fragileThemes
                                    : [{ label: 'No fragile theme locked in yet', evidence: 'When a concept keeps needing support, it will appear here.' }]).map((item, index) => (
                                    <article key={`fragile-theme-${index}`} className='instructorFireflyDiagnosisCard'>
                                      <div>
                                        <span>Theme</span>
                                        <p>{item.label}</p>
                                      </div>
                                      {item.evidence && (
                                        <div>
                                          <span>Evidence</span>
                                          <p>{item.evidence}</p>
                                        </div>
                                      )}
                                    </article>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className='instructorFireflyTwoCol'>
                              <div className='instructorFireflySection instructorFireflySoftCard'>
                                <h6>Misconception recurrence</h6>
                                <div className='instructorFireflyDiagnosisList'>
                                  {(selectedGeneratedFireflyProfile.diagnosticLens.misconceptionRecurrence.length
                                    ? selectedGeneratedFireflyProfile.diagnosticLens.misconceptionRecurrence
                                    : selectedGeneratedFireflyProfile.conceptMasteryMap.misconceptionThreads.length
                                      ? selectedGeneratedFireflyProfile.conceptMasteryMap.misconceptionThreads
                                      : [{ label: 'No recurring misconception locked in yet', evidence: 'Repeated misconceptions will be listed here as Firefly gathers more evidence.' }]).map((item, index) => (
                                    <article key={`misconception-${index}`} className='instructorFireflyDiagnosisCard'>
                                      <div>
                                        <span>Pattern</span>
                                        <p>{item.label}</p>
                                      </div>
                                      {item.evidence && (
                                        <div>
                                          <span>Evidence</span>
                                          <p>{item.evidence}</p>
                                        </div>
                                      )}
                                      {item.action && (
                                        <div>
                                          <span>Recommended move</span>
                                          <p>{item.action}</p>
                                        </div>
                                      )}
                                    </article>
                                  ))}
                                </div>
                              </div>

                              <div className='instructorFireflySection instructorFireflySoftCard'>
                                <h6>Your next session plan</h6>
                                <div className='instructorFireflyChipGroup'>
                                  {selectedGeneratedFireflyProfile.studentActionPlan.keepDoing.map((item) => (
                                    <span key={`keep-${item}`} className='instructorFireflyChip'>{item}</span>
                                  ))}
                                  {selectedGeneratedFireflyProfile.studentActionPlan.focusNextSessions.map((item) => (
                                    <span key={`focus-next-${item}`} className='instructorFireflyChip instructorFireflyChipAlt'>{item}</span>
                                  ))}
                                  {selectedGeneratedFireflyProfile.studentActionPlan.beforeNextSession.map((item) => (
                                    <span key={`before-next-${item}`} className='instructorFireflyChip instructorFireflyChipNeutral'>{item}</span>
                                  ))}
                                </div>
                                {selectedGeneratedFireflyProfile.studentActionPlan.nextChallenge && (
                                  <p className='instructorFireflyDetailLine'><strong>Next challenge:</strong> {selectedGeneratedFireflyProfile.studentActionPlan.nextChallenge}</p>
                                )}
                                {(selectedGeneratedFireflyProfile.studentFacing.reflectionPrompt || selectedGeneratedFireflyProfile.studentActionPlan.reflectionPrompt) && (
                                  <p className='instructorFireflyDetailLine'><strong>Reflection prompt:</strong> {selectedGeneratedFireflyProfile.studentFacing.reflectionPrompt || selectedGeneratedFireflyProfile.studentActionPlan.reflectionPrompt}</p>
                                )}
                              </div>
                            </div>

                            <div className='instructorFireflyTwoCol'>
                              <div className='instructorFireflySection instructorFireflySoftCard'>
                                <h6>Student-facing note</h6>
                                <p>{selectedGeneratedFireflyProfile.studentFacing.encouragement || 'Keep using one focused Firefly goal at a time so the tutoring signal sharpens faster.'}</p>
                                {selectedGeneratedFireflyProfile.studentFacing.whyThisMatters && (
                                  <p className='instructorFireflyDetailLine'><strong>Why this matters:</strong> {selectedGeneratedFireflyProfile.studentFacing.whyThisMatters}</p>
                                )}
                              </div>

                              <div className='instructorFireflySection instructorFireflySoftCard'>
                                <h6>Firefly tutoring playbook</h6>
                                <div className='instructorFireflyPlaybookGrid'>
                                  <div>
                                    <span>Opening moves</span>
                                    {(selectedGeneratedFireflyProfile.tutoringPlaybook.openingMoves.length
                                      ? selectedGeneratedFireflyProfile.tutoringPlaybook.openingMoves
                                      : ['Start by reconnecting to the last supported concept and checking recall.']).map((item, index) => (
                                      <p key={`opening-${index}`}>{item}</p>
                                    ))}
                                  </div>
                                  <div>
                                    <span>In-session moves</span>
                                    {(selectedGeneratedFireflyProfile.tutoringPlaybook.inSessionMoves.length
                                      ? selectedGeneratedFireflyProfile.tutoringPlaybook.inSessionMoves
                                      : ['Use guided explanation, short checks, and visible worked examples.']).map((item, index) => (
                                      <p key={`move-${index}`}>{item}</p>
                                    ))}
                                  </div>
                                  <div>
                                    <span>Checks for understanding</span>
                                    {(selectedGeneratedFireflyProfile.tutoringPlaybook.checksForUnderstanding.length
                                      ? selectedGeneratedFireflyProfile.tutoringPlaybook.checksForUnderstanding
                                      : ['Ask the learner to explain the concept back in their own words.']).map((item, index) => (
                                      <p key={`check-${index}`}>{item}</p>
                                    ))}
                                  </div>
                                  <div>
                                    <span>Board and visual strategies</span>
                                    {(selectedGeneratedFireflyProfile.tutoringPlaybook.boardAndVisualStrategies.length
                                      ? selectedGeneratedFireflyProfile.tutoringPlaybook.boardAndVisualStrategies
                                      : ['Break the concept into short visual steps or show a worked example sequence.']).map((item, index) => (
                                      <p key={`board-${index}`}>{item}</p>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className='instructorFireflySignals instructorFireflyFullSignals'>
                            <div>
                              <span>Strengths</span>
                              {(selectedFireflyBundle.profile?.strengths?.length ? selectedFireflyBundle.profile.strengths : ['No strengths captured yet.']).map((item) => (
                                <small key={`strength-${item}`}>{item}</small>
                              ))}
                            </div>
                            <div>
                              <span>Gaps to brush on</span>
                              {(selectedFireflyBundle.profile?.struggles?.length ? selectedFireflyBundle.profile.struggles : ['No learning gaps captured yet.']).map((item) => (
                                <small key={`struggle-${item}`}>{item}</small>
                              ))}
                            </div>
                            <div>
                              <span>Next steps</span>
                              {(selectedFireflyBundle.profile?.recommendedNextSteps?.length ? selectedFireflyBundle.profile.recommendedNextSteps : ['Keep watching upcoming Firefly sessions.']).map((item) => (
                                <small key={`next-${item}`}>{item}</small>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      selectedFireflyBundle.reports.length > 0 ? (
                        <div className='instructorFireflyReader instructorFireflyReaderFull'>
                          <div className='instructorFireflyTimeline'>
                            {selectedFireflyBundle.reports.map((report) => (
                              <button
                                key={report.id}
                                type='button'
                                className={[
                                  selectedFireflyReport?.id === report.id ? 'active' : '',
                                  fireflyReadMap[buildFireflyReadId(instructorId, selectedFireflyBundle.studentId, report.id)] ? '' : 'unread'
                                ].filter(Boolean).join(' ')}
                                onClick={() => setSelectedFireflyReportId(report.id)}
                              >
                                <span>{formatFireflyDate(report.endedAtMs || report.startedAtMs)}</span>
                                <strong>{report.sessionFocus || report.topicsCovered[0] || 'Firefly session'}</strong>
                                <small>{formatFireflyDuration(report.durationSeconds)}</small>
                                {!fireflyReadMap[buildFireflyReadId(instructorId, selectedFireflyBundle.studentId, report.id)] && (
                                  <em>New</em>
                                )}
                              </button>
                            ))}
                          </div>

                          {selectedFireflyReport && (
                            <article className='instructorFireflyReport'>
                              <div className='instructorFireflyReportTop'>
                                <div>
                                  <span className='instructorCoursesEyebrow'>Individual report</span>
                                  <h5>{selectedFireflyReport.sessionFocus || 'Firefly learning session'}</h5>
                                  <p>{selectedFireflyReport.summary || 'No written summary was captured for this session.'}</p>
                                </div>
                                <strong>{formatFireflyDuration(selectedFireflyReport.durationSeconds)}</strong>
                              </div>

                              <div className='instructorFireflyReportMeta'>
                                <span>{selectedFireflyReport.confidenceTrend || 'Confidence signal pending'}</span>
                                <span>{selectedFireflyReport.learningPace || 'Pace pending'}</span>
                                <span>{selectedFireflyReport.userTurns} student turns</span>
                              </div>

                              <div className='instructorFireflyReportGrid'>
                                <div>
                                  <h6>Topics covered</h6>
                                  {(selectedFireflyReport.topicsCovered.length ? selectedFireflyReport.topicsCovered : ['No topics captured.']).map((item) => <p key={`topic-${item}`}>{item}</p>)}
                                </div>
                                <div>
                                  <h6>Teaching methods</h6>
                                  {(selectedFireflyReport.teachingMethodsUsed.length ? selectedFireflyReport.teachingMethodsUsed : selectedFireflyReport.preferredTeachingModes.length ? selectedFireflyReport.preferredTeachingModes : ['No teaching methods captured.']).map((item) => <p key={`mode-${item}`}>{item}</p>)}
                                </div>
                                <div>
                                  <h6>Support strategies</h6>
                                  {(selectedFireflyReport.supportStrategies.length ? selectedFireflyReport.supportStrategies : ['No support strategies captured.']).map((item) => <p key={`support-${item}`}>{item}</p>)}
                                </div>
                                <div>
                                  <h6>Recommended next steps</h6>
                                  {(selectedFireflyReport.recommendedNextSteps.length ? selectedFireflyReport.recommendedNextSteps : ['No next steps captured.']).map((item) => <p key={`step-${item}`}>{item}</p>)}
                                </div>
                              </div>

                              {selectedFireflyReport.diagnoses.length > 0 && (
                                <div className='instructorFireflyDiagnosis'>
                                  <h6>Misconceptions and intervention</h6>
                                  {selectedFireflyReport.diagnoses.map((diagnosis, index) => (
                                    <div key={`${diagnosis.topic}-${index}`}>
                                      <strong>{diagnosis.topic || diagnosis.subtopic || 'Learning gap'}</strong>
                                      <p>{diagnosis.misconception || diagnosis.evidence || 'Firefly flagged this as an area to revisit.'}</p>
                                      {diagnosis.bestIntervention && <small>{diagnosis.bestIntervention}</small>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </article>
                          )}
                        </div>
                      ) : (
                        <div className='instructorFireflyEmpty'>
                          <h6>No Firefly sessions yet</h6>
                          <p>Once this student uses Ubunifu Firefly, their individual reports and learning profile will appear here for you.</p>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className='instructorFireflyStudentGrid'>
                    <article className='instructorFireflyOverviewCard'>
                      <span className='instructorCoursesEyebrow'>Class signal</span>
                      <h5>{activeCourseFireflyRows.filter((row) => row.reports.length > 0).length} active Firefly learners</h5>
                      <p>{activeCourseUnreadFireflyReportCount > 0 ? `${activeCourseUnreadFireflyReportCount} new report${activeCourseUnreadFireflyReportCount === 1 ? '' : 's'} need review.` : 'Open a learner profile to see their general report, charts, learning gaps, and individual session reports.'}</p>
                    </article>

                    {activeCourseFireflyRows.map((row) => {
                      const studentName = row.student.name || row.student.studentEmail || 'Student'
                      const latestReport = row.reports[0]
                      return (
                        <button
                          key={row.studentId}
                          type='button'
                          className='instructorFireflyLearnerCard'
                          onClick={() => {
                            setSelectedFireflyStudentId(row.studentId)
                            setSelectedFireflyReportId('')
                            setFireflyReportMode('general')
                            setIsFireflyProfileOpen(true)
                          }}
                        >
                          <span className='instructorCourseStudentAvatar instructorFireflyAvatar'>
                            {row.student.profilePic ? (
                              <img src={row.student.profilePic} alt='' />
                            ) : (
                              studentName.charAt(0).toUpperCase()
                            )}
                          </span>
                          <span className='instructorFireflyLearnerCopy'>
                            <strong>{studentName}</strong>
                            <small>{row.generatedProfile?.learnerSnapshot || row.profile?.summary || latestReport?.summary || 'No Firefly summary yet.'}</small>
                          </span>
                          <span className='instructorFireflyLearnerStats'>
                            <b>{row.reports.length}</b>
                            <small>reports</small>
                          </span>
                          {row.unreadReports.length > 0 && (
                            <span className='instructorFireflyLearnerUnread'>{row.unreadReports.length} new</span>
                          )}
                          <span className='instructorFireflyOpenLabel'>Open learner profile</span>
                        </button>
                      )
                    })}
                  </div>
                )
              ) : (
                <p className='instructorCourseMuted'>No students connected to {getCourseName(activeCourse)} have been added yet, so there are no Firefly reports to show.</p>
              )}
            </section>
          )}

          {activeTab === 'assignments' && (
            <section className='instructorCoursePanel instructorCoursePanelFull'>
              <div className='instructorCoursePanelHeader'>
                <div>
                  <span className='instructorCoursesEyebrow'>Assignments</span>
                  <h5>Issue and track course work</h5>
                </div>
                <span className='instructorCourseCount'>{activeCourseAssignments.length} assignment{activeCourseAssignments.length === 1 ? '' : 's'}</span>
              </div>

              <div className='instructorAssignmentLayout'>
                <form className='instructorAssignmentForm' onSubmit={createAssignment}>
                  <div className='instructorAssignmentChoiceGrid'>
                    <div>
                      <label>Assignment type</label>
                      <select
                        value={assignmentForm.assignmentType}
                        onChange={(event) => updateAssignmentType(event.target.value)}
                      >
                        {assignmentTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>Assign to</label>
                      <select
                        value={assignmentForm.audience}
                        onChange={(event) => updateAssignmentForm('audience', event.target.value)}
                      >
                        <option value='all'>All students in this class</option>
                        <option value='selected'>Selected students only</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label>Assignment title</label>
                    <input
                      type='text'
                      value={assignmentForm.title}
                      onChange={(event) => updateAssignmentForm('title', event.target.value)}
                      placeholder='e.g. Build a responsive landing page'
                    />
                  </div>

                  <div>
                    <label>
                      {currentAssignmentTypeConfig?.supportsPromptItems ? 'Assignment brief' : 'Instructions'}
                    </label>
                    <div className='instructorAssignmentEditorShell'>
                      <div className='instructorAssignmentEditorTools'>
                        <button type='button' onClick={() => applyInstructionStyle('bold')} aria-label='Bold'><FontAwesomeIcon icon={faBold} /></button>
                        <button type='button' onClick={() => applyInstructionStyle('underline')} aria-label='Underline'><FontAwesomeIcon icon={faUnderline} /></button>
                        <button type='button' onClick={insertInstructionList} aria-label='List'><FontAwesomeIcon icon={faListUl} /></button>
                        <button type='button' onClick={insertInstructionCode} aria-label='Code'><FontAwesomeIcon icon={faCode} /></button>
                      </div>
                      <div
                        className='instructorAssignmentEditor'
                        ref={assignmentEditorRef}
                        contentEditable
                        role='textbox'
                        aria-multiline='true'
                        data-placeholder={
                          assignmentForm.assignmentType === 'questions_text'
                            ? 'Add a short brief or context for the written questions below.'
                            : assignmentForm.assignmentType === 'questions_code'
                              ? 'Add a short brief or coding context for the prompts below.'
                              : assignmentForm.assignmentType === 'code_link'
                                ? 'Describe the one code task and ask for a GitHub repository link.'
                                : assignmentForm.assignmentType === 'code_paste'
                                  ? 'Describe the one code task and ask students to paste their solution.'
                                  : 'Explain what they should build and ask them to submit a project link or demo.'
                        }
                        onInput={updateAssignmentInstructions}
                      />
                    </div>
                  </div>

                  {currentAssignmentTypeConfig?.supportsPromptItems && (
                    <div className='instructorAssignmentPromptBuilder'>
                      <div className='instructorAssignmentPromptHeader'>
                        <div>
                          <label>Questions or prompts</label>
                          <p>
                            {assignmentForm.assignmentType === 'questions_code'
                              ? 'Each prompt will ask the student for a separate code answer.'
                              : 'Add each question separately so students can answer them one by one.'}
                          </p>
                        </div>
                        <button
                          type='button'
                          className='instructorAssignmentPromptAdd'
                          onClick={addAssignmentPrompt}
                        >
                          Add question
                        </button>
                      </div>

                      <div className='instructorAssignmentPromptList'>
                        {(assignmentForm.promptItems || []).map((prompt, index) => (
                          <div className='instructorAssignmentPromptRow' key={`${prompt.id}-${index}-${assignmentForm.promptItems.length}`}>
                            <div className='instructorAssignmentPromptTop'>
                              <span>Q{index + 1}</span>
                              <div className='instructorAssignmentEditorTools'>
                                <button type='button' onClick={() => applyPromptStyle(prompt.id, 'bold')} aria-label='Bold'><FontAwesomeIcon icon={faBold} /></button>
                                <button type='button' onClick={() => applyPromptStyle(prompt.id, 'underline')} aria-label='Underline'><FontAwesomeIcon icon={faUnderline} /></button>
                                <button type='button' onClick={() => insertPromptList(prompt.id)} aria-label='List'><FontAwesomeIcon icon={faListUl} /></button>
                                <button type='button' onClick={() => insertPromptCode(prompt.id)} aria-label='Code'><FontAwesomeIcon icon={faCode} /></button>
                              </div>
                            </div>
                            <div
                              className='instructorAssignmentPromptEditor'
                              ref={(node) => {
                                if (node) {
                                  promptEditorRefs.current[prompt.id] = node
                                } else {
                                  delete promptEditorRefs.current[prompt.id]
                                }
                              }}
                              contentEditable
                              suppressContentEditableWarning
                              role='textbox'
                              aria-multiline='true'
                              data-placeholder={
                                assignmentForm.assignmentType === 'questions_code'
                                  ? 'e.g. Write a function that validates an email address.'
                                  : 'e.g. Explain the difference between supervised and unsupervised learning.'
                              }
                              onInput={() => updateAssignmentPromptHtml(prompt.id)}
                            />
                            <button
                              type='button'
                              className='instructorAssignmentPromptRemove'
                              onClick={() => removeAssignmentPrompt(prompt.id)}
                              disabled={(assignmentForm.promptItems || []).length <= 1}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className='instructorAssignmentFormGrid'>
                    <div>
                      <label>Teaching plan week</label>
                      <select
                        value={assignmentForm.assignmentWeekId}
                        onChange={(event) => updateAssignmentForm('assignmentWeekId', event.target.value)}
                        disabled={!schemeWeekOptions.length}
                      >
                        <option value=''>
                          {schemeWeekOptions.length ? 'Select a week from the teaching plan' : 'No weekly teaching plan available yet'}
                        </option>
                        {schemeWeekOptions.map((option) => (
                          <option key={option.id} value={option.id}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label>Due date</label>
                      <input
                        type='date'
                        value={assignmentForm.dueDate}
                        onChange={(event) => updateAssignmentForm('dueDate', event.target.value)}
                      />
                    </div>
                    <div>
                      <label>Resource link</label>
                      <input
                        type='url'
                        value={assignmentForm.resourceLink}
                        onChange={(event) => updateAssignmentForm('resourceLink', event.target.value)}
                        placeholder='https://...'
                      />
                    </div>
                    <div>
                      <label>Assignment file</label>
                      <input
                        ref={assignmentAttachmentInputRef}
                        type='file'
                        onChange={handleAssignmentAttachmentChange}
                      />
                      {assignmentAttachmentFile && (
                        <p className='instructorAssignmentNotice'>
                          Selected file: {assignmentAttachmentFile.name}{' '}
                          {formatFileSize(assignmentAttachmentFile.size) ? `(${formatFileSize(assignmentAttachmentFile.size)})` : ''}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className='instructorAssignmentTarget'>
                    <span>{activeCourseClasses.length} class{activeCourseClasses.length === 1 ? '' : 'es'}</span>
                    <span>
                      {assignmentForm.audience === 'all'
                        ? activeCourseStudentCount
                        : assignmentForm.selectedStudentIds.length} student{(assignmentForm.audience === 'all' ? activeCourseStudentCount : assignmentForm.selectedStudentIds.length) === 1 ? '' : 's'}
                    </span>
                    <span>{getCourseName(activeCourse)}</span>
                    {selectedAssignmentWeek && <span>{selectedAssignmentWeek.label}</span>}
                  </div>

                  {assignmentForm.audience === 'selected' && (
                    <div className='instructorAssignmentStudentPicker'>
                      {activeCourseStudents.length > 0 ? (
                        activeCourseStudents.map((studentItem) => {
                          const studentId = studentItem.studentID || studentItem.userId || studentItem.id
                          return (
                            <label key={studentId}>
                              <input
                                type='checkbox'
                                checked={assignmentForm.selectedStudentIds.includes(studentId)}
                                onChange={() => toggleAssignmentStudent(studentId)}
                              />
                              <span>{studentItem.name || studentItem.studentEmail || 'Student'}</span>
                            </label>
                          )
                        })
                      ) : (
                        <p>No students are connected to this course yet.</p>
                      )}
                    </div>
                  )}

                  {assignmentNotice && <p className='instructorAssignmentNotice'>{assignmentNotice}</p>}

                  <button type='submit' disabled={savingAssignment}>
                    {savingAssignment ? 'Publishing...' : 'Publish Assignment'}
                  </button>
                </form>
              </div>
            </section>
          )}
        </main>
      </section>
    </div>
  )
}

export default MyCoursesComp
